import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useNavigation } from '../hooks/useNavigation';
import ssomaService from '../services/ssomaService';
import empleadoService from '../services/empleadoService';
import configuracionPlanillaAlternativaService from '../services/configuracionPlanillaAlternativaService';
import configuracionFotosPersonalService from '../services/configuracionFotosPersonalService';
import atributoService from '../services/atributoService';
import SsomaTallasTab from '../components/SsomaTallasTab';
import { generarConstanciaEppPdf } from '../utils/generarConstanciaEppPdf';
import {
  etiquetaModalidadFichaSsoma,
  fichaCoincideModalidad,
  modalidadFichaSsoma,
  mostrarEtiquetaModalidadSsoma,
} from '../utils/ssomaFichaModalidad';

const TABS = [
  { id: 'fichas', label: 'Fichas' },
  { id: 'sctr', label: 'SCTR' },
  { id: 'accidentes', label: 'Accidentes' },
  { id: 'catalogo-epp', label: 'Catálogo EPP' },
  { id: 'inspecciones', label: 'Inspecciones' },
  { id: 'iper', label: 'IPER' },
];

function iniciales(nombre) {
  return String(nombre || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();
}

function fmtFecha(fecha) {
  if (!fecha) return '—';
  const texto = String(fecha).split('T')[0];
  const [y, m, d] = texto.split('-');
  return y && m && d ? `${d}/${m}/${y}` : texto;
}

function fechaInput(valor) {
  if (!valor) return '';
  return String(valor).split('T')[0];
}

function badgeEstado(estado) {
  if (estado === 'vencido') return 'bg-red-100 text-red-800';
  if (estado === 'por_vencer') return 'bg-amber-100 text-amber-800';
  if (estado === 'vigente') return 'bg-green-100 text-green-800';
  return 'bg-gray-100 text-gray-600';
}

function tonoResumen(estado) {
  if (estado === 'vencido') {
    return { caja: 'bg-red-50 border-red-100', marca: 'bg-red-500', texto: 'text-red-800', valor: 'text-red-900' };
  }
  if (estado === 'por_vencer') {
    return { caja: 'bg-amber-50 border-amber-100', marca: 'bg-amber-500', texto: 'text-amber-800', valor: 'text-amber-900' };
  }
  if (estado === 'vigente') {
    return { caja: 'bg-emerald-50 border-emerald-100', marca: 'bg-emerald-500', texto: 'text-emerald-800', valor: 'text-emerald-900' };
  }
  if (estado === 'alerta') {
    return { caja: 'bg-rose-50 border-rose-100', marca: 'bg-rose-500', texto: 'text-rose-800', valor: 'text-rose-900' };
  }
  return { caja: 'bg-slate-50 border-slate-100', marca: 'bg-slate-300', texto: 'text-slate-500', valor: 'text-slate-700' };
}

function labelEstado(estado) {
  if (estado === 'vencido') return 'Vencido';
  if (estado === 'por_vencer') return 'Por vencer';
  if (estado === 'vigente') return 'Vigente';
  return 'Sin registro';
}

function valorProyecto(ficha) {
  return String(ficha?.proyecto || '').trim() || 'Sin proyecto';
}

function valorArea(ficha) {
  return String(ficha?.areas_de_trabajo || '').trim() || 'Sin área';
}

function labelTipoAccidente(tipo) {
  if (tipo === 'incidente') return 'Incidente';
  if (tipo === 'casi_accidente') return 'Casi accidente';
  return 'Accidente';
}

function labelEstadoAccidente(estado) {
  if (estado === 'en_investigacion') return 'En investigación';
  if (estado === 'cerrado') return 'Cerrado';
  return 'Abierto';
}

function claseEstadoAccidente(estado) {
  if (estado === 'cerrado') return 'bg-slate-200 text-slate-700';
  if (estado === 'en_investigacion') return 'bg-amber-100 text-amber-800';
  return 'bg-rose-100 text-rose-800';
}

function BadgeEstadoAccidente({ estado }) {
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${claseEstadoAccidente(estado)}`}>
      {labelEstadoAccidente(estado)}
    </span>
  );
}

function etiquetaPestanaAccidente(accidente) {
  return `${labelTipoAccidente(accidente?.tipo)} ${fmtFecha(accidente?.fecha_hora)}`;
}

function labelTipoEmo(tipo) {
  if (tipo === 'preocupacional') return 'Preocupacional';
  if (tipo === 'retiro') return 'Retiro';
  if (tipo === 'cambio_puesto') return 'Cambio de puesto';
  if (tipo === 'reincorporacion') return 'Reincorporación';
  return 'Periódico';
}

function labelAptitudEmo(aptitud) {
  if (aptitud === 'apto_con_restricciones') return 'Apto con restricciones';
  if (aptitud === 'no_apto') return 'No apto';
  if (aptitud === 'apto') return 'Apto';
  return 'Sin aptitud';
}

function descargarArchivoLocal(archivo) {
  const url = URL.createObjectURL(archivo);
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.download = archivo.name || 'constancia-epp.pdf';
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1500);
}

function itemsConstanciaDesdeMovimientos(movimientos) {
  return (movimientos || []).map((m) => ({
    nombre: m.catalogo?.nombre || m.nombre || 'EPP',
    cantidad: m.cantidad || 1,
  }));
}

function labelTipoEpp(tipo) {
  if (tipo === 'devolucion') return 'Devolución';
  if (tipo === 'reposicion') return 'Reposición';
  return 'Entrega';
}

function extraerAreasCatalogo(subAtributos) {
  const keys = [
    'Áreas',
    'Areas',
    'Areas de Trabajo',
    'Área de Trabajo',
    'areas_de_trabajo',
    'area_de_trabajo',
    'AREAS DE TRABAJO',
    'Area de Trabajo',
  ];
  for (const key of keys) {
    if (Array.isArray(subAtributos?.[key]) && subAtributos[key].length) {
      return subAtributos[key];
    }
  }
  const encontrada = Object.keys(subAtributos || {}).find((key) => key.toLowerCase().includes('area'));
  return encontrada && Array.isArray(subAtributos[encontrada]) ? subAtributos[encontrada] : [];
}

function labelInvolucradosDocumento(documento, involucradosCaso = []) {
  const asignados = documento?.involucrados || [];
  if (!asignados.length) return 'Sin asignar';
  if (involucradosCaso.length > 1 && asignados.length === involucradosCaso.length) {
    return 'Todos los involucrados';
  }
  return asignados.map((e) => e.nombre_completo).join(', ');
}

const Ssoma = () => {
  const { selectedCompany } = useAuth();
  const { ssomaActivado } = useNavigation();
  const [searchParams, setSearchParams] = useSearchParams();
  const empresaId = selectedCompany?.id;

  const [tab, setTab] = useState('fichas');
  const [q, setQ] = useState('');
  const [filtroPersonal, setFiltroPersonal] = useState('Activo');
  const [filtroProyecto, setFiltroProyecto] = useState('');
  const [filtroArea, setFiltroArea] = useState('');
  const [filtroTipoPlanilla, setFiltroTipoPlanilla] = useState('');
  const [planillaInternaActiva, setPlanillaInternaActiva] = useState(false);
  const [fotosPersonalActivo, setFotosPersonalActivo] = useState(false);
  const [accidentes, setAccidentes] = useState([]);
  const [filtroEstadoAcc, setFiltroEstadoAcc] = useState('todos');
  const [formAcc, setFormAcc] = useState({
    fecha_hora: '',
    tipo: 'accidente',
    gravedad: 'leve',
    lugar: '',
    descripcion: '',
    involucrados: [],
    busqueda: '',
  });
  const [areasCatalogo, setAreasCatalogo] = useState([]);
  const [fichas, setFichas] = useState([]);
  const [alertas, setAlertas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mensaje, setMensaje] = useState('');
  const [error, setError] = useState('');

  const [fichaAbierta, setFichaAbierta] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [cargandoFicha, setCargandoFicha] = useState(false);

  const [eppData, setEppData] = useState({ catalogo: [], movimientos: [] });
  const [cursos, setCursos] = useState([]);
  const [inspecciones, setInspecciones] = useState([]);
  const [iper, setIper] = useState([]);

  const [edicion, setEdicion] = useState({ tipo: '', id: null });
  const [fichaSctr, setFichaSctr] = useState({ tipo: 'ambos', aseguradora: '', numero: '', fecha_inicio: '', fecha_vencimiento: '', archivo: null });
  const [fichaEmo, setFichaEmo] = useState({ tipo: 'periodico', clinica: '', aptitud: 'apto', fecha_examen: '', fecha_vencimiento: '', archivo: null });
  const [fichaEpp, setFichaEpp] = useState({
    tipo: 'entrega',
    fecha: new Date().toISOString().slice(0, 10),
    fecha_vencimiento: '',
    items: [],
    nuevo: '',
  });
  const [fichaCurso, setFichaCurso] = useState({ curso_id: '', nuevo: '', tipo: 'capacitacion', fecha: '', fecha_vencimiento: '', archivo: null });
  const [fichaAcc, setFichaAcc] = useState({
    fecha_hora: '',
    tipo: 'accidente',
    gravedad: 'leve',
    lugar: '',
    descripcion: '',
    alcance: 'personal',
    involucrados: [],
    busqueda: '',
  });
  const [fichaOtro, setFichaOtro] = useState({ titulo: '', archivo: null });
  const [pestanasAccidente, setPestanasAccidente] = useState([]);
  const [detalleAccidente, setDetalleAccidente] = useState(null);
  const [cargandoAccidente, setCargandoAccidente] = useState(false);
  const [docAccidente, setDocAccidente] = useState({ titulo: '', archivos: [], involucrados: [] });
  const [cerrandoAccidente, setCerrandoAccidente] = useState(false);
  const [guardandoFicha, setGuardandoFicha] = useState('');
  const [formCatalogoEpp, setFormCatalogoEpp] = useState({ nombre: '', vida_util_dias: '' });
  const [guardandoCatalogo, setGuardandoCatalogo] = useState(false);
  const [formIns, setFormIns] = useState({ fecha: '', tipo: 'planeada', area: '', inspector: '', hallazgo: '', archivo: null });
  const [formIper, setFormIper] = useState({ nombre: '', version: '', cargo: '', area: '', actividad: '', peligro: '', riesgo: '', archivo: null });
  const [sctrMasivo, setSctrMasivo] = useState({
    tipo: 'ambos',
    aseguradora: '',
    numero: '',
    fecha_inicio: '',
    fecha_vencimiento: '',
    archivo: null,
  });
  const [sctrMasivoIds, setSctrMasivoIds] = useState([]);
  const [guardandoSctrMasivo, setGuardandoSctrMasivo] = useState(false);
  const [filtroSctr, setFiltroSctr] = useState('renovar');

  const mostrar = (tipo, texto) => {
    if (tipo === 'error') setError(texto);
    else setMensaje(texto);
    setTimeout(() => {
      setError('');
      setMensaje('');
    }, 3500);
  };

  const cargarFichas = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    const [resp, alertasResp] = await Promise.all([
      ssomaService.fichas(empresaId),
      ssomaService.alertasDashboard(empresaId),
    ]);
    if (resp.success) setFichas(resp.data || []);
    else setError(resp.error || 'No se pudieron cargar las fichas');
    if (alertasResp.success) setAlertas(alertasResp.data);
    setLoading(false);
  }, [empresaId]);

  const abrirFicha = async (empleadoId) => {
    setFichaAbierta(empleadoId);
    setCargandoFicha(true);
    const [resp, eppResp, cursosResp] = await Promise.all([
      ssomaService.ficha(empresaId, empleadoId),
      ssomaService.epp(empresaId),
      ssomaService.cursos(empresaId),
    ]);
    if (resp.success) {
      setDetalle(resp.data);
      setFichas((prev) => prev.map((f) => (
        Number(f.id) === Number(empleadoId) ? { ...f, nuevo: false } : f
      )));
    } else mostrar('error', resp.error);
    if (eppResp.success) setEppData(eppResp.data || { catalogo: [], movimientos: [] });
    if (cursosResp.success) setCursos(cursosResp.data || []);
    setEdicion({ tipo: '', id: null });
    setCargandoFicha(false);
  };

  const subirEscaneoSeccion = async (tipo, archivo, titulo, fechaVencimiento, extra = {}) => {
    if (!archivo || !fichaAbierta) return { success: true };
    return ssomaService.subirDocumento(empresaId, {
      tipo,
      archivo,
      empleadoId: fichaAbierta,
      titulo,
      fechaVencimiento,
      ...extra,
    });
  };

  const confirmarBorrado = (texto) => window.confirm(texto || '¿Borrar este expediente y su archivo?');

  const cancelarEdicion = () => {
    setEdicion({ tipo: '', id: null });
    setFichaSctr({ tipo: 'ambos', aseguradora: '', numero: '', fecha_inicio: '', fecha_vencimiento: '', archivo: null });
    setFichaEmo({ tipo: 'periodico', clinica: '', aptitud: 'apto', fecha_examen: '', fecha_vencimiento: '', archivo: null });
    setFichaEpp({
      tipo: 'entrega',
      fecha: new Date().toISOString().slice(0, 10),
      fecha_vencimiento: '',
      items: [],
      nuevo: '',
    });
    setFichaCurso({ curso_id: '', nuevo: '', tipo: 'capacitacion', fecha: '', fecha_vencimiento: '', archivo: null });
    setFormIns({ fecha: '', tipo: 'planeada', area: '', inspector: '', hallazgo: '', archivo: null });
    setFormIper({ nombre: '', version: '', cargo: '', area: '', actividad: '', peligro: '', riesgo: '', archivo: null });
    setFormCatalogoEpp({ nombre: '', vida_util_dias: '' });
  };

  const docsDe = (tipo) => (detalle?.documentos || []).filter((d) => d.tipo === tipo);

  const cargarAccidente = useCallback(async (accidenteId) => {
    if (!empresaId || !accidenteId) return;
    setCargandoAccidente(true);
    const resp = await ssomaService.verAccidente(empresaId, accidenteId);
    if (resp.success) {
      setDetalleAccidente(resp.data);
      const involucrados = resp.data?.empleados || [];
      if (involucrados.length === 1) {
        setDocAccidente((prev) => ({ ...prev, involucrados: [String(involucrados[0].id)] }));
      }
    } else mostrar('error', resp.error || 'No se pudo abrir el accidente');
    setCargandoAccidente(false);
  }, [empresaId]);

  const abrirPestanaAccidente = async (accidente) => {
    if (!accidente?.id) return;
    setPestanasAccidente((prev) => (
      prev.some((p) => Number(p.id) === Number(accidente.id))
        ? prev
        : [...prev, { id: accidente.id, label: etiquetaPestanaAccidente(accidente) }]
    ));
    setTab(`accidente-${accidente.id}`);
    setFichaAbierta(null);
    await cargarAccidente(accidente.id);
  };

  const cerrarPestanaAccidente = (accidenteId) => {
    setPestanasAccidente((prev) => prev.filter((p) => Number(p.id) !== Number(accidenteId)));
    if (tab === `accidente-${accidenteId}`) {
      setTab('fichas');
      setDetalleAccidente(null);
    }
  };

  const cargarTab = useCallback(async (actual) => {
    if (!empresaId) return;
    if (actual === 'inspecciones') {
      const resp = await ssomaService.inspecciones(empresaId);
      if (resp.success) setInspecciones(resp.data || []);
    }
    if (actual === 'catalogo-epp') {
      const resp = await ssomaService.epp(empresaId);
      if (resp.success) setEppData(resp.data || { catalogo: [], movimientos: [] });
    }
    if (actual === 'iper') {
      const resp = await ssomaService.iper(empresaId);
      if (resp.success) setIper(resp.data || []);
    }
    if (actual === 'accidentes') {
      const resp = await ssomaService.accidentes(empresaId);
      if (resp.success) setAccidentes(resp.data || []);
    }
    if (String(actual || '').startsWith('accidente-')) {
      const id = String(actual).replace('accidente-', '');
      await cargarAccidente(id);
    }
  }, [empresaId, cargarAccidente]);

  useEffect(() => {
    if (empresaId && ssomaActivado) {
      cargarFichas();
    }
  }, [empresaId, ssomaActivado, cargarFichas]);

  useEffect(() => {
    const cargarPlanillaInterna = async () => {
      if (!empresaId) {
        setPlanillaInternaActiva(false);
        return;
      }
      try {
        const response = await configuracionPlanillaAlternativaService.verificarEstado(empresaId);
        setPlanillaInternaActiva(Boolean(response?.data?.planilla_alternativa_activa));
      } catch (err) {
        setPlanillaInternaActiva(false);
      }
    };
    cargarPlanillaInterna();
    const onConfigActualizada = () => cargarPlanillaInterna();
    window.addEventListener('configuracion-planilla-alternativa-actualizada', onConfigActualizada);
    return () => window.removeEventListener('configuracion-planilla-alternativa-actualizada', onConfigActualizada);
  }, [empresaId]);

  useEffect(() => {
    const cargarFotosPersonal = async () => {
      if (!empresaId) {
        setFotosPersonalActivo(false);
        return;
      }
      try {
        const response = await configuracionFotosPersonalService.verificarEstado(empresaId);
        setFotosPersonalActivo(Boolean(response?.data?.fotos_personal_activo ?? response?.data?.activo));
      } catch (err) {
        setFotosPersonalActivo(false);
      }
    };
    cargarFotosPersonal();
    const onConfigActualizada = () => cargarFotosPersonal();
    window.addEventListener('configuracion-fotos-personal-actualizada', onConfigActualizada);
    return () => window.removeEventListener('configuracion-fotos-personal-actualizada', onConfigActualizada);
  }, [empresaId]);

  useEffect(() => {
    const cargarAreas = async () => {
      if (!empresaId) {
        setAreasCatalogo([]);
        return;
      }
      const resp = await atributoService.obtenerConfiguracionCompleta(empresaId);
      if (resp.success) {
        setAreasCatalogo(extraerAreasCatalogo(resp.data?.sub_atributos || {}));
      }
    };
    cargarAreas();
  }, [empresaId]);

  const proyectosUnicos = useMemo(
    () => [...new Set(fichas.map(valorProyecto))].sort((a, b) => a.localeCompare(b, 'es')),
    [fichas]
  );
  const areasUnicas = useMemo(
    () => [...new Set(fichas.map(valorArea))].sort((a, b) => a.localeCompare(b, 'es')),
    [fichas]
  );

  const fichasPorFiltros = useMemo(() => {
    const termino = q.trim().toLowerCase();
    return fichas.filter((ficha) => {
      if (filtroProyecto && valorProyecto(ficha) !== filtroProyecto) return false;
      if (filtroArea && valorArea(ficha) !== filtroArea) return false;
      if (filtroTipoPlanilla && !fichaCoincideModalidad(ficha, filtroTipoPlanilla)) return false;
      if (termino) {
        const texto = [
          ficha.nombre_completo,
          ficha.numero_documento,
          ficha.cargo,
          ficha.proyecto,
          ficha.areas_de_trabajo,
        ].join(' ').toLowerCase();
        if (!texto.includes(termino)) return false;
      }
      return true;
    });
  }, [fichas, q, filtroProyecto, filtroArea, filtroTipoPlanilla]);

  const fichasVisibles = useMemo(
    () => fichasPorFiltros
      .filter((f) => String(f.estado || '').toLowerCase() === filtroPersonal.toLowerCase())
      .slice()
      .sort((a, b) => Number(Boolean(b.nuevo)) - Number(Boolean(a.nuevo))),
    [fichasPorFiltros, filtroPersonal]
  );

  const estadoSctrFicha = (ficha) => String(ficha?.sctr?.estado || 'sin');

  const gruposSctr = useMemo(() => {
    const porEstado = {
      sin: fichasVisibles.filter((f) => estadoSctrFicha(f) === 'sin'),
      vencido: fichasVisibles.filter((f) => estadoSctrFicha(f) === 'vencido'),
      por_vencer: fichasVisibles.filter((f) => estadoSctrFicha(f) === 'por_vencer'),
      vigente: fichasVisibles.filter((f) => estadoSctrFicha(f) === 'vigente'),
    };
    if (filtroSctr === 'sin') {
      return [{ id: 'sin', titulo: 'Sin SCTR', items: porEstado.sin }];
    }
    if (filtroSctr === 'vencido') {
      return [{ id: 'vencido', titulo: 'SCTR vencido', items: porEstado.vencido }];
    }
    if (filtroSctr === 'renovar') {
      return [
        { id: 'sin', titulo: 'Sin SCTR', items: porEstado.sin },
        { id: 'vencido', titulo: 'SCTR vencido', items: porEstado.vencido },
      ];
    }
    return [
      { id: 'sin', titulo: 'Sin SCTR', items: porEstado.sin },
      { id: 'vencido', titulo: 'SCTR vencido', items: porEstado.vencido },
      { id: 'por_vencer', titulo: 'SCTR por vencer', items: porEstado.por_vencer },
      { id: 'vigente', titulo: 'SCTR vigente', items: porEstado.vigente },
    ];
  }, [fichasVisibles, filtroSctr]);

  const fichasSctrVisibles = useMemo(
    () => gruposSctr.flatMap((grupo) => grupo.items),
    [gruposSctr]
  );

  const conteoSctr = useMemo(() => ({
    sin: fichasVisibles.filter((f) => estadoSctrFicha(f) === 'sin').length,
    vencido: fichasVisibles.filter((f) => estadoSctrFicha(f) === 'vencido').length,
    por_vencer: fichasVisibles.filter((f) => estadoSctrFicha(f) === 'por_vencer').length,
  }), [fichasVisibles]);

  const cantidadActivo = fichasPorFiltros.filter((f) => String(f.estado || '').toLowerCase() === 'activo').length;
  const cantidadCesado = fichasPorFiltros.filter((f) => String(f.estado || '').toLowerCase() === 'cesado').length;

  const accidentesVisibles = useMemo(() => {
    if (filtroEstadoAcc === 'cerrado') return accidentes.filter((a) => a.estado === 'cerrado');
    if (filtroEstadoAcc === 'abiertos') return accidentes.filter((a) => a.estado !== 'cerrado');
    return accidentes;
  }, [accidentes, filtroEstadoAcc]);

  useEffect(() => {
    if (empresaId && ssomaActivado && tab !== 'fichas') {
      cargarTab(tab);
    }
  }, [tab, empresaId, ssomaActivado, cargarTab]);

  useEffect(() => {
    if (!ssomaActivado) return;
    const empleadoParam = searchParams.get('empleado');
    const accidenteParam = searchParams.get('accidente');
    if (empleadoParam) {
      setTab('fichas');
      abrirFicha(empleadoParam);
    } else if (accidenteParam) {
      abrirPestanaAccidente({ id: accidenteParam, tipo: 'accidente' });
    }
    if (empleadoParam || accidenteParam) {
      const next = new URLSearchParams(searchParams);
      next.delete('empleado');
      next.delete('accidente');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, ssomaActivado]);

  useEffect(() => {
    if (!ssomaActivado) return undefined;
    const onAbrir = (event) => {
      const empleadoId = event.detail?.empleadoId;
      const accidenteId = event.detail?.accidenteId;
      if (empleadoId) {
        setTab('fichas');
        abrirFicha(empleadoId);
      } else if (accidenteId) {
        abrirPestanaAccidente({ id: accidenteId, tipo: 'accidente' });
      }
    };
    window.addEventListener('ssoma-abrir-notificacion', onAbrir);
    return () => window.removeEventListener('ssoma-abrir-notificacion', onAbrir);
  }, [ssomaActivado]);

  if (!ssomaActivado) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center text-gray-600">
        El módulo SSOMA no está activo para esta empresa. Actívalo en Configuración.
      </div>
    );
  }

  const recargarTodo = async () => {
    await cargarFichas();
    if (fichaAbierta) await abrirFicha(fichaAbierta);
    if (tab !== 'fichas') await cargarTab(tab);
  };

  const resetSctrMasivo = () => {
    setSctrMasivo({
      tipo: 'ambos',
      aseguradora: '',
      numero: '',
      fecha_inicio: '',
      fecha_vencimiento: '',
      archivo: null,
    });
    setSctrMasivoIds([]);
  };

  const toggleSctrMasivoId = (id) => {
    const key = String(id);
    setSctrMasivoIds((prev) => (prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]));
  };

  const seleccionarFiltradosSctrMasivo = () => {
    setSctrMasivoIds(fichasSctrVisibles.map((f) => String(f.id)));
  };

  const onSctrMasivo = async (e) => {
    e.preventDefault();
    if (!sctrMasivo.archivo) {
      mostrar('error', 'Selecciona el documento SCTR');
      return;
    }
    if (!sctrMasivoIds.length) {
      mostrar('error', 'Selecciona al menos un trabajador');
      return;
    }
    setGuardandoSctrMasivo(true);
    const resp = await ssomaService.guardarSctrMasivo(empresaId, {
      ...sctrMasivo,
      empleadoIds: sctrMasivoIds,
    });
    setGuardandoSctrMasivo(false);
    if (!resp.success) {
      mostrar('error', resp.error || 'No se pudo cargar el SCTR');
      return;
    }
    mostrar('ok', `SCTR cargado para ${resp.data?.cantidad || sctrMasivoIds.length} trabajador(es)`);
    resetSctrMasivo();
    await recargarTodo();
  };

  const onFichaSctr = async (e) => {
    e.preventDefault();
    setGuardandoFicha('sctr');
    const payload = {
      empleado_id: fichaAbierta,
      tipo: fichaSctr.tipo,
      aseguradora: fichaSctr.aseguradora,
      numero: fichaSctr.numero,
      fecha_inicio: fichaSctr.fecha_inicio || null,
      fecha_vencimiento: fichaSctr.fecha_vencimiento,
    };
    const editando = edicion.tipo === 'sctr' && edicion.id;
    const resp = editando
      ? await ssomaService.actualizarSctr(empresaId, edicion.id, payload)
      : await ssomaService.guardarSctr(empresaId, payload);
    if (!resp.success) {
      setGuardandoFicha('');
      mostrar('error', resp.error);
      return;
    }
    const registroId = resp.data?.id || edicion.id;
    const doc = await subirEscaneoSeccion('sctr', fichaSctr.archivo, `SCTR ${fichaSctr.numero || ''}`.trim(), fichaSctr.fecha_vencimiento, { sctrId: registroId });
    setGuardandoFicha('');
    if (!doc.success) mostrar('error', doc.error || 'SCTR guardado, pero no se subió el archivo');
    else mostrar('ok', editando ? 'Expediente SCTR actualizado' : 'Nuevo expediente SCTR guardado');
    cancelarEdicion();
    recargarTodo();
  };

  const onFichaEmo = async (e) => {
    e.preventDefault();
    setGuardandoFicha('emo');
    const payload = {
      empleado_id: fichaAbierta,
      tipo: fichaEmo.tipo,
      clinica: fichaEmo.clinica,
      aptitud: fichaEmo.aptitud,
      fecha_examen: fichaEmo.fecha_examen || null,
      fecha_vencimiento: fichaEmo.fecha_vencimiento,
    };
    const editando = edicion.tipo === 'emo' && edicion.id;
    const resp = editando
      ? await ssomaService.actualizarEmo(empresaId, edicion.id, payload)
      : await ssomaService.guardarEmo(empresaId, payload);
    if (!resp.success) {
      setGuardandoFicha('');
      mostrar('error', resp.error);
      return;
    }
    const registroId = resp.data?.id || edicion.id;
    const doc = await subirEscaneoSeccion('emo', fichaEmo.archivo, `EMO ${labelTipoEmo(fichaEmo.tipo)}`.trim(), fichaEmo.fecha_vencimiento, { emoId: registroId });
    setGuardandoFicha('');
    if (!doc.success) mostrar('error', doc.error || 'EMO guardado, pero no se subió el archivo');
    else mostrar('ok', editando ? 'Expediente EMO actualizado' : 'Nuevo expediente EMO guardado');
    cancelarEdicion();
    recargarTodo();
  };

  const toggleEppCatalogo = (item) => {
    setFichaEpp((prev) => {
      const ya = prev.items.some((epp) => String(epp.catalogo_id) === String(item.id));
      if (ya) {
        return { ...prev, items: prev.items.filter((epp) => String(epp.catalogo_id) !== String(item.id)) };
      }
      return {
        ...prev,
        items: [...prev.items, {
          key: `cat-${item.id}`,
          catalogo_id: item.id,
          nombre: item.talla ? `${item.nombre} (${item.talla})` : item.nombre,
          cantidad: 1,
        }],
      };
    });
  };

  const agregarEppNuevo = () => {
    const nombre = String(fichaEpp.nuevo || '').trim();
    if (!nombre) return;
    setFichaEpp((prev) => ({
      ...prev,
      nuevo: '',
      items: [...prev.items, { key: `nuevo-${Date.now()}`, catalogo_id: null, nombre, cantidad: 1 }],
    }));
  };

  const onFichaEpp = async (e) => {
    e.preventDefault();
    if (!fichaEpp.items.length) {
      mostrar('error', 'Marca o agrega los EPP que se están entregando');
      return;
    }
    if (!fichaEpp.fecha) {
      mostrar('error', 'Indica la fecha de entrega');
      return;
    }

    setGuardandoFicha('epp');
    const itemsRegistrados = [];
    for (const item of fichaEpp.items) {
      let catalogoId = item.catalogo_id;
      if (!catalogoId) {
        const cat = await ssomaService.guardarEppCatalogo(empresaId, { nombre: item.nombre });
        if (!cat.success) {
          setGuardandoFicha('');
          mostrar('error', cat.error || `No se pudo crear ${item.nombre}`);
          return;
        }
        catalogoId = cat.data.id;
      }
      itemsRegistrados.push({
        ...item,
        catalogo_id: catalogoId,
        cantidad: Number(item.cantidad) || 1,
      });
    }

    const payloadEntrega = {
      empleado_id: fichaAbierta,
      tipo: fichaEpp.tipo,
      fecha: fichaEpp.fecha,
      items: itemsRegistrados.map((item) => ({
        catalogo_id: item.catalogo_id,
        cantidad: item.cantidad,
        fecha_vencimiento: fichaEpp.fecha_vencimiento || null,
      })),
    };
    const editando = edicion.tipo === 'epp' && edicion.id;
    const resp = editando
      ? await ssomaService.actualizarEppEntrega(empresaId, edicion.id, payloadEntrega)
      : await ssomaService.guardarEppEntrega(empresaId, payloadEntrega);
    if (!resp.success) {
      setGuardandoFicha('');
      mostrar('error', resp.error || 'No se pudo guardar el registro de EPP');
      return;
    }

    try {
      const foto = await empleadoService.obtenerFotoCarnetDataUrl(detalle?.empleado?.id);
      const pdf = await generarConstanciaEppPdf({
        empresa: selectedCompany,
        empleado: detalle?.empleado,
        tallas: detalle?.tallas,
        items: itemsRegistrados,
        fecha: fichaEpp.fecha,
        tipo: fichaEpp.tipo,
        numero: resp.data?.numero,
        foto,
      });
      const doc = await ssomaService.subirDocumento(empresaId, {
        tipo: 'epp',
        archivo: pdf,
        empleadoId: fichaAbierta,
        titulo: `Constancia ${labelTipoEpp(fichaEpp.tipo)} N.º ${resp.data?.numero || ''}`.trim(),
        fechaDocumento: fichaEpp.fecha,
        eppEntregaId: resp.data?.id || edicion.id,
      });
      descargarArchivoLocal(pdf);
      setGuardandoFicha('');
      if (!doc.success) mostrar('error', doc.error || 'Registro guardado, pero no se archivó la constancia');
      else mostrar('ok', editando
        ? `${labelTipoEpp(fichaEpp.tipo)} N.º ${resp.data?.numero} actualizada`
        : `${labelTipoEpp(fichaEpp.tipo)} N.º ${resp.data?.numero} guardada en el expediente`);
    } catch (err) {
      setGuardandoFicha('');
      mostrar('error', err.message || 'Registro guardado, pero no se pudo generar la constancia');
    }

    cancelarEdicion();
    recargarTodo();
  };

  const descargarConstanciaEpp = async (registro) => {
    if (registro?.documentos?.length) {
      const archivo = registro.documentos[0];
      try {
        await ssomaService.descargarDocumento(empresaId, archivo.id, archivo.archivo_nombre);
        mostrar('ok', `Constancia ${labelTipoEpp(registro.tipo)} N.º ${registro.numero} descargada`);
      } catch (err) {
        mostrar('error', err.message || 'No se pudo descargar la constancia archivada');
      }
      return;
    }

    const items = itemsConstanciaDesdeMovimientos(registro?.movimientos);
    if (!items.length) {
      mostrar('error', 'Ese registro no tiene EPP para la constancia');
      return;
    }
    setGuardandoFicha(`constancia-${registro.id}`);
    try {
      const foto = await empleadoService.obtenerFotoCarnetDataUrl(detalle?.empleado?.id);
      const pdf = await generarConstanciaEppPdf({
        empresa: selectedCompany,
        empleado: detalle?.empleado,
        tallas: detalle?.tallas,
        items,
        fecha: registro.fecha,
        tipo: registro.tipo || 'entrega',
        numero: registro.numero,
        foto,
      });
      descargarArchivoLocal(pdf);
      mostrar('ok', `Constancia ${labelTipoEpp(registro.tipo)} N.º ${registro.numero} descargada`);
    } catch (err) {
      mostrar('error', err.message || 'No se pudo generar la constancia');
    } finally {
      setGuardandoFicha('');
    }
  };

  const onFichaCurso = async (e) => {
    e.preventDefault();
    setGuardandoFicha('curso');
    const editando = edicion.tipo === 'curso' && edicion.id;
    let cursoId = fichaCurso.curso_id;
    if (!editando && !cursoId && fichaCurso.nuevo) {
      const curso = await ssomaService.guardarCurso(empresaId, {
        nombre: fichaCurso.nuevo,
        tipo: fichaCurso.tipo,
      });
      if (!curso.success) {
        setGuardandoFicha('');
        mostrar('error', curso.error);
        return;
      }
      cursoId = curso.data.id;
    }
    if (!editando && !cursoId) {
      setGuardandoFicha('');
      mostrar('error', 'Elige un curso o escribe uno nuevo');
      return;
    }
    const resp = editando
      ? await ssomaService.actualizarCapacitacion(empresaId, edicion.id, {
        fecha: fichaCurso.fecha,
        fecha_vencimiento: fichaCurso.fecha_vencimiento || null,
      })
      : await ssomaService.guardarSesion(empresaId, {
        curso_id: cursoId,
        fecha: fichaCurso.fecha,
        empleados: [fichaAbierta],
      });
    if (!resp.success) {
      setGuardandoFicha('');
      mostrar('error', resp.error);
      return;
    }
    const asistenciaId = editando
      ? edicion.id
      : (resp.data?.asistencias || []).find((a) => String(a.empleado_id) === String(fichaAbierta))?.id;
    const doc = await subirEscaneoSeccion('curso', fichaCurso.archivo, 'Constancia / asistencia', fichaCurso.fecha_vencimiento, { asistenciaId });
    setGuardandoFicha('');
    if (!doc.success) mostrar('error', doc.error || 'Capacitación guardada, pero no se subió el archivo');
    else mostrar('ok', editando ? 'Expediente de capacitación actualizado' : 'Nuevo expediente de capacitación guardado');
    cancelarEdicion();
    recargarTodo();
  };

  const borrarExpediente = async (tipo, id, esArchivo = false) => {
    if (!confirmarBorrado(esArchivo ? '¿Quitar este archivo del expediente?' : '¿Borrar este expediente y su archivo?')) return;
    setGuardandoFicha(`borrar-${tipo}-${id}`);
    let resp = { success: false, error: 'No se pudo borrar' };
    if (esArchivo || tipo === 'otro') resp = await ssomaService.eliminarDocumento(empresaId, id);
    else if (tipo === 'sctr') resp = await ssomaService.eliminarSctr(empresaId, id);
    else if (tipo === 'emo') resp = await ssomaService.eliminarEmo(empresaId, id);
    else if (tipo === 'epp') resp = await ssomaService.eliminarEppEntrega(empresaId, id);
    else if (tipo === 'curso') resp = await ssomaService.eliminarCapacitacion(empresaId, id);
    else if (tipo === 'inspeccion') resp = await ssomaService.eliminarInspeccion(empresaId, id);
    else if (tipo === 'iper') resp = await ssomaService.eliminarIper(empresaId, id);
    setGuardandoFicha('');
    if (!resp.success) {
      mostrar('error', resp.error);
      return;
    }
    mostrar('ok', esArchivo ? 'Archivo eliminado' : 'Expediente eliminado');
    if (edicion.id === id) cancelarEdicion();
    if (tipo === 'inspeccion' || (esArchivo && tab === 'inspecciones')) {
      cargarTab('inspecciones');
      return;
    }
    if (tipo === 'iper' || (esArchivo && tab === 'iper')) {
      cargarTab('iper');
      return;
    }
    recargarTodo();
  };

  const eppCatalogoUsado = (item) => Number(item?.movimientos_count || 0) > 0;

  const empezarEditarCatalogoEpp = (item) => {
    setEdicion({ tipo: 'catalogo-epp', id: item.id });
    setFormCatalogoEpp({
      nombre: item.nombre || '',
      vida_util_dias: item.vida_util_dias ? String(item.vida_util_dias) : '',
    });
  };

  const onGuardarCatalogoEpp = async (e) => {
    e.preventDefault();
    if (!formCatalogoEpp.nombre.trim()) {
      mostrar('error', 'Escribe el nombre del EPP');
      return;
    }
    const payload = {
      nombre: formCatalogoEpp.nombre.trim(),
      vida_util_dias: formCatalogoEpp.vida_util_dias ? Number(formCatalogoEpp.vida_util_dias) : null,
    };
    setGuardandoCatalogo(true);
    const editando = edicion.tipo === 'catalogo-epp' && edicion.id;
    const resp = editando
      ? await ssomaService.actualizarEppCatalogo(empresaId, edicion.id, payload)
      : await ssomaService.guardarEppCatalogo(empresaId, payload);
    setGuardandoCatalogo(false);
    if (!resp.success) {
      mostrar('error', resp.error || 'No se pudo guardar el EPP');
      return;
    }
    mostrar('ok', editando ? `${resp.data.nombre} se actualizó` : `${resp.data.nombre} se agregó al catálogo`);
    cancelarEdicion();
    cargarTab('catalogo-epp');
  };

  const borrarCatalogoEpp = async (item) => {
    if (eppCatalogoUsado(item)) {
      mostrar('error', 'No se puede borrar: este EPP ya se usó en una entrega.');
      return;
    }
    if (!confirmarBorrado('¿Borrar este EPP del catálogo?')) return;
    setGuardandoCatalogo(true);
    const resp = await ssomaService.eliminarEppCatalogo(empresaId, item.id);
    setGuardandoCatalogo(false);
    if (!resp.success) {
      mostrar('error', resp.error || 'No se pudo borrar el EPP');
      return;
    }
    mostrar('ok', 'EPP eliminado del catálogo');
    if (edicion.tipo === 'catalogo-epp' && Number(edicion.id) === Number(item.id)) cancelarEdicion();
    cargarTab('catalogo-epp');
  };

  const empezarEditarInspeccion = (item) => {
    setEdicion({ tipo: 'inspeccion', id: item.id });
    setFormIns({
      fecha: fechaInput(item.fecha),
      tipo: item.tipo || 'planeada',
      area: item.area || '',
      inspector: item.inspector || '',
      hallazgo: item.hallazgos?.[0]?.descripcion || '',
      archivo: null,
    });
  };

  const empezarEditarIper = (item) => {
    setEdicion({ tipo: 'iper', id: item.id });
    setFormIper({
      nombre: item.nombre || '',
      version: item.version || '',
      cargo: item.cargo || '',
      area: item.area || '',
      actividad: item.filas?.[0]?.actividad || '',
      peligro: item.filas?.[0]?.peligro || '',
      riesgo: item.filas?.[0]?.riesgo || '',
      archivo: null,
    });
  };

  const onGuardarInspeccion = async (e) => {
    e.preventDefault();
    const payload = {
      fecha: formIns.fecha,
      tipo: formIns.tipo,
      area: formIns.area,
      inspector: formIns.inspector,
      hallazgos: formIns.hallazgo ? [{ descripcion: formIns.hallazgo, estado: 'abierto' }] : [],
    };
    setGuardandoFicha('inspeccion');
    const editando = edicion.tipo === 'inspeccion' && edicion.id;
    const resp = editando
      ? await ssomaService.actualizarInspeccion(empresaId, edicion.id, payload)
      : await ssomaService.guardarInspeccion(empresaId, payload);
    if (!resp.success) {
      setGuardandoFicha('');
      mostrar('error', resp.error);
      return;
    }
    const registroId = resp.data?.id || edicion.id;
    if (formIns.archivo) {
      const doc = await ssomaService.subirDocumento(empresaId, {
        tipo: 'inspeccion',
        archivo: formIns.archivo,
        titulo: `Inspección ${formIns.area || formIns.fecha}`,
        inspeccionId: registroId,
      });
      setGuardandoFicha('');
      if (!doc.success) mostrar('error', doc.error || 'Inspección guardada, pero no se subió el archivo');
      else mostrar('ok', editando ? 'Inspección actualizada' : 'Inspección registrada');
    } else {
      setGuardandoFicha('');
      mostrar('ok', editando ? 'Inspección actualizada' : 'Inspección registrada');
    }
    cancelarEdicion();
    cargarTab('inspecciones');
  };

  const onGuardarIper = async (e) => {
    e.preventDefault();
    const payload = {
      nombre: formIper.nombre,
      version: formIper.version,
      cargo: formIper.cargo,
      area: formIper.area,
      filas: formIper.actividad || formIper.peligro || formIper.riesgo ? [{
        actividad: formIper.actividad,
        peligro: formIper.peligro,
        riesgo: formIper.riesgo,
      }] : [],
    };
    setGuardandoFicha('iper');
    const editando = edicion.tipo === 'iper' && edicion.id;
    const resp = editando
      ? await ssomaService.actualizarIper(empresaId, edicion.id, payload)
      : await ssomaService.guardarIper(empresaId, payload);
    if (!resp.success) {
      setGuardandoFicha('');
      mostrar('error', resp.error);
      return;
    }
    const registroId = resp.data?.id || edicion.id;
    if (formIper.archivo) {
      const doc = await ssomaService.subirDocumento(empresaId, {
        tipo: 'iper',
        archivo: formIper.archivo,
        titulo: formIper.nombre || 'Matriz IPER',
        iperId: registroId,
      });
      setGuardandoFicha('');
      if (!doc.success) mostrar('error', doc.error || 'Matriz guardada, pero no se subió el archivo');
      else mostrar('ok', editando ? 'Matriz IPER actualizada' : 'Matriz IPER guardada');
    } else {
      setGuardandoFicha('');
      mostrar('ok', editando ? 'Matriz IPER actualizada' : 'Matriz IPER guardada');
    }
    cancelarEdicion();
    cargarTab('iper');
  };

  const empezarEditarSctr = (s) => {
    setEdicion({ tipo: 'sctr', id: s.id });
    setFichaSctr({
      tipo: s.tipo || 'ambos',
      aseguradora: s.aseguradora || '',
      numero: s.numero || '',
      fecha_inicio: fechaInput(s.fecha_inicio),
      fecha_vencimiento: fechaInput(s.fecha_vencimiento),
      archivo: null,
    });
  };

  const empezarEditarEmo = (e) => {
    setEdicion({ tipo: 'emo', id: e.id });
    setFichaEmo({
      tipo: e.tipo || 'periodico',
      clinica: e.clinica || '',
      aptitud: e.aptitud || 'apto',
      fecha_examen: fechaInput(e.fecha_examen),
      fecha_vencimiento: fechaInput(e.fecha_vencimiento),
      archivo: null,
    });
  };

  const empezarEditarEpp = (registro) => {
    setEdicion({ tipo: 'epp', id: registro.id });
    setFichaEpp({
      tipo: registro.tipo || 'entrega',
      fecha: fechaInput(registro.fecha) || new Date().toISOString().slice(0, 10),
      fecha_vencimiento: '',
      nuevo: '',
      items: (registro.movimientos || []).map((m) => ({
        key: `mov-${m.id}`,
        catalogo_id: m.catalogo_id,
        nombre: m.catalogo?.nombre || 'EPP',
        cantidad: m.cantidad || 1,
      })),
    });
  };

  const empezarEditarCurso = (c) => {
    setEdicion({ tipo: 'curso', id: c.id });
    setFichaCurso({
      curso_id: c.sesion?.curso_id || '',
      nuevo: '',
      tipo: c.sesion?.curso?.tipo || 'capacitacion',
      fecha: fechaInput(c.sesion?.fecha),
      fecha_vencimiento: fechaInput(c.fecha_vencimiento),
      archivo: null,
    });
  };

  const onFichaAcc = async (e) => {
    e.preventDefault();
    if (fichaAcc.alcance === 'multiple' && (fichaAcc.involucrados || []).length === 0) {
      mostrar('error', 'Marca al menos a otro trabajador involucrado');
      return;
    }
    setGuardandoFicha('accidente');
    const involucrados = fichaAcc.alcance === 'multiple'
      ? Array.from(new Set([String(fichaAbierta), ...(fichaAcc.involucrados || []).map(String)]))
      : [String(fichaAbierta)];
    const resp = await ssomaService.guardarAccidente(empresaId, {
      fecha_hora: fichaAcc.fecha_hora,
      tipo: fichaAcc.tipo,
      gravedad: fichaAcc.gravedad,
      lugar: fichaAcc.lugar,
      descripcion: fichaAcc.descripcion,
      empleados: involucrados,
    });
    setGuardandoFicha('');
    if (!resp.success) {
      mostrar('error', resp.error);
      return;
    }
    mostrar('ok', involucrados.length > 1
      ? `Accidente abierto para ${involucrados.length} trabajadores. Adjunta los documentos en su pestaña.`
      : 'Accidente abierto. Adjunta los documentos en su pestaña.');
    setFichaAcc({
      fecha_hora: '',
      tipo: 'accidente',
      gravedad: 'leve',
      lugar: '',
      descripcion: '',
      alcance: 'personal',
      involucrados: [],
      busqueda: '',
    });
    await recargarTodo();
    await abrirPestanaAccidente(resp.data);
  };

  const onTabAcc = async (e) => {
    e.preventDefault();
    if ((formAcc.involucrados || []).length === 0) {
      mostrar('error', 'Selecciona al menos un trabajador involucrado');
      return;
    }
    setGuardandoFicha('accidente-tab');
    const resp = await ssomaService.guardarAccidente(empresaId, {
      fecha_hora: formAcc.fecha_hora,
      tipo: formAcc.tipo,
      gravedad: formAcc.gravedad,
      lugar: formAcc.lugar,
      descripcion: formAcc.descripcion,
      empleados: formAcc.involucrados,
    });
    setGuardandoFicha('');
    if (!resp.success) {
      mostrar('error', resp.error);
      return;
    }
    mostrar('ok', formAcc.involucrados.length > 1
      ? `Accidente abierto para ${formAcc.involucrados.length} trabajadores. Adjunta los documentos en su pestaña.`
      : 'Accidente abierto. Adjunta los documentos en su pestaña.');
    setFormAcc({
      fecha_hora: '',
      tipo: 'accidente',
      gravedad: 'leve',
      lugar: '',
      descripcion: '',
      involucrados: [],
      busqueda: '',
    });
    await recargarTodo();
    await cargarTab('accidentes');
    await abrirPestanaAccidente(resp.data);
  };

  const onAgregarDocsAccidente = async (e) => {
    e.preventDefault();
    const accidenteId = detalleAccidente?.id;
    if (!accidenteId) return;
    const archivos = Array.from(docAccidente.archivos || []);
    const involucrados = (docAccidente.involucrados || []).map(String);
    if (archivos.length === 0) {
      mostrar('error', 'Selecciona uno o más documentos');
      return;
    }
    if (involucrados.length === 0) {
      mostrar('error', 'Elige a quién corresponde este documento');
      return;
    }
    setGuardandoFicha('docs-accidente');
    let ok = 0;
    for (const archivo of archivos) {
      const resp = await ssomaService.subirDocumento(empresaId, {
        tipo: 'accidente',
        archivo,
        accidenteId,
        titulo: docAccidente.titulo || archivo.name,
        empleadoId: involucrados[0],
        empleados: involucrados,
      });
      if (resp.success) ok += 1;
      else {
        mostrar('error', resp.error || 'No se pudo subir un documento');
        break;
      }
    }
    setGuardandoFicha('');
    if (ok > 0) {
      mostrar('ok', ok === 1 ? 'Documento agregado al accidente' : `${ok} documentos agregados al accidente`);
      setDocAccidente((prev) => ({ ...prev, titulo: '', archivos: [] }));
      await cargarAccidente(accidenteId);
      recargarTodo();
    }
  };

  const onQuitarDocAccidente = async (documentoId) => {
    const resp = await ssomaService.eliminarDocumento(empresaId, documentoId);
    if (!resp.success) {
      mostrar('error', resp.error);
      return;
    }
    mostrar('ok', 'Documento eliminado');
    if (detalleAccidente?.id) await cargarAccidente(detalleAccidente.id);
  };

  const onCerrarAccidente = async () => {
    if (!detalleAccidente?.id) return;
    if (!window.confirm('¿Cerrar este accidente? Ya no se podrán agregar ni quitar documentos.')) return;
    setCerrandoAccidente(true);
    const resp = await ssomaService.actualizarAccidente(empresaId, detalleAccidente.id, { estado: 'cerrado' });
    setCerrandoAccidente(false);
    if (!resp.success) {
      mostrar('error', resp.error);
      return;
    }
    mostrar('ok', 'Accidente cerrado');
    setDetalleAccidente(resp.data);
    recargarTodo();
  };

  const onFichaOtro = async (e) => {
    e.preventDefault();
    if (!fichaOtro.archivo) {
      mostrar('error', 'Selecciona el escaneo');
      return;
    }
    setGuardandoFicha('otro');
    const doc = await subirEscaneoSeccion('otro', fichaOtro.archivo, fichaOtro.titulo || 'Documento SSOMA');
    setGuardandoFicha('');
    if (!doc.success) mostrar('error', doc.error);
    else {
      mostrar('ok', 'Documento subido');
      setFichaOtro({ titulo: '', archivo: null });
      recargarTodo();
    }
  };

  return (
    <div className="space-y-5">
      {(mensaje || error) && (
        <div className={`rounded-lg px-4 py-3 text-sm ${error ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'}`}>
          {error || mensaje}
        </div>
      )}

      {alertas && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <AlertaMini titulo="SCTR vencidos" valor={alertas.totales?.sctr_vencidos} color="red" />
          <AlertaMini titulo="EMO vencidos" valor={alertas.totales?.emo_vencidos} color="red" />
          <AlertaMini titulo="SCTR / EMO por vencer" valor={(alertas.totales?.sctr_por_vencer || 0) + (alertas.totales?.emo_por_vencer || 0)} color="amber" />
          <AlertaMini titulo="Accidentes abiertos" valor={alertas.totales?.accidentes_abiertos} color="rose" />
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium ${
              tab === item.id ? 'bg-teal-600 text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {item.label}
          </button>
        ))}
        {pestanasAccidente.map((item) => {
          const id = `accidente-${item.id}`;
          const activo = tab === id;
          return (
            <div
              key={id}
              className={`inline-flex items-center rounded-full border text-sm font-medium ${
                activo ? 'bg-rose-600 text-white border-rose-600' : 'bg-white border-rose-200 text-rose-800'
              }`}
            >
              <button type="button" onClick={() => setTab(id)} className="px-3 py-1.5">
                {item.label}
              </button>
              <button
                type="button"
                onClick={() => cerrarPestanaAccidente(item.id)}
                className={`pr-2 pl-0 text-xs ${activo ? 'text-white/80 hover:text-white' : 'text-rose-500 hover:text-rose-800'}`}
                title="Cerrar pestaña"
              >
                ×
              </button>
            </div>
          );
        })}
      </div>

      {tab === 'fichas' && (
        <div className="space-y-4">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <div className="inline-flex rounded-full border border-gray-200 bg-white p-1">
                {['Activo', 'Cesado'].map((estado) => {
                  const cantidad = estado === 'Activo' ? cantidadActivo : cantidadCesado;
                  const activo = filtroPersonal === estado;
                  return (
                    <button
                      key={estado}
                      type="button"
                      onClick={() => setFiltroPersonal(estado)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                        activo ? 'bg-teal-600 text-white' : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {estado === 'Activo' ? 'Personal activo' : 'Personal cesado'} ({cantidad})
                    </button>
                  );
                })}
              </div>
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar por nombre, DNI, cargo o proyecto"
                className="w-full md:w-96 border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div className={`grid grid-cols-1 sm:grid-cols-2 ${planillaInternaActiva ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-3`}>
              <select
                value={filtroProyecto}
                onChange={(e) => setFiltroProyecto(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
              >
                <option value="">Todos los proyectos</option>
                {proyectosUnicos.map((proyecto) => (
                  <option key={proyecto} value={proyecto}>{proyecto}</option>
                ))}
              </select>
              <select
                value={filtroArea}
                onChange={(e) => setFiltroArea(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
              >
                <option value="">Todas las áreas</option>
                {areasUnicas.map((area) => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>
              <select
                value={filtroTipoPlanilla}
                onChange={(e) => setFiltroTipoPlanilla(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
              >
                <option value="">Todas las modalidades</option>
                <option value="rh">RH (Recibo por Honorarios)</option>
                {planillaInternaActiva && (
                  <>
                    <option value="dual">Dual (PLAME + interna)</option>
                    <option value="interno">Solo interna</option>
                  </>
                )}
              </select>
              {(filtroProyecto || filtroArea || filtroTipoPlanilla || q) && (
                <button
                  type="button"
                  onClick={() => {
                    setQ('');
                    setFiltroProyecto('');
                    setFiltroArea('');
                    setFiltroTipoPlanilla('');
                  }}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white hover:bg-gray-50"
                >
                  Limpiar filtros
                </button>
              )}
            </div>
          </div>
          {loading ? (
            <p className="text-sm text-gray-500">Cargando fichas...</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {fichasVisibles.map((ficha) => (
                <button
                  key={ficha.id}
                  type="button"
                  onClick={() => abrirFicha(ficha.id)}
                  className={`text-left rounded-2xl border shadow-sm p-5 hover:shadow-md transition ${
                    ficha.nuevo
                      ? 'bg-emerald-50 border-emerald-300 hover:border-emerald-400'
                      : 'bg-white border-gray-100 hover:border-teal-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {fotosPersonalActivo && ficha.foto_carnet_url ? (
                      <img
                        src={empleadoService.urlFotoCarnet(ficha.foto_carnet_url)}
                        alt={ficha.nombre_completo}
                        className="h-12 w-12 rounded-full object-cover shrink-0"
                      />
                    ) : (
                    <div className={`h-12 w-12 rounded-full flex items-center justify-center text-white font-semibold shrink-0 ${
                      String(ficha.estado || '').toLowerCase() === 'cesado'
                        ? 'bg-gradient-to-r from-gray-400 to-gray-500'
                        : 'bg-gradient-to-r from-teal-500 to-emerald-600'
                    }`}>
                      {iniciales(ficha.nombre_completo)}
                    </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900 truncate">{ficha.nombre_completo}</p>
                        {ficha.nuevo && (
                          <span className="shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-600 text-white">Nuevo</span>
                        )}
                        {String(ficha.estado || '').toLowerCase() === 'cesado' && (
                          <span className="shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-800">Cesado</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">{ficha.numero_documento} · {ficha.cargo || 'Sin cargo'}</p>
                      <p className="text-xs text-gray-400 truncate">{valorProyecto(ficha)} · {valorArea(ficha)}</p>
                      {mostrarEtiquetaModalidadSsoma(ficha, planillaInternaActiva) && (
                        <p className="text-[10px] text-amber-700 mt-0.5">
                          {etiquetaModalidadFichaSsoma(modalidadFichaSsoma(ficha))}
                        </p>
                      )}
                    </div>
                  </div>
                  <ResumenFicha ficha={ficha} />
                </button>
              ))}
              {fichasVisibles.length === 0 && (
                <p className="text-sm text-gray-500 col-span-full">
                  {filtroProyecto || filtroArea || filtroTipoPlanilla || q
                    ? 'No hay personal que coincida con los filtros.'
                    : filtroPersonal === 'Cesado'
                      ? 'No hay personal cesado para mostrar.'
                      : 'No hay personal activo para mostrar.'}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'sctr' && (
        <SeccionCard titulo="SCTR">
          <p className="text-sm text-gray-600 mb-4">
            Carga un solo documento y las mismas fechas de vigencia para varios trabajadores. La ficha individual sigue sirviendo para un caso puntual.
          </p>
          <form onSubmit={onSctrMasivo} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <select
                className="border rounded-lg px-3 py-2 text-sm"
                value={sctrMasivo.tipo}
                onChange={(e) => setSctrMasivo({ ...sctrMasivo, tipo: e.target.value })}
              >
                <option value="ambos">Pensión y salud</option>
                <option value="pension">Pensión</option>
                <option value="salud">Salud</option>
              </select>
              <input
                className="border rounded-lg px-3 py-2 text-sm"
                placeholder="Aseguradora"
                value={sctrMasivo.aseguradora}
                onChange={(e) => setSctrMasivo({ ...sctrMasivo, aseguradora: e.target.value })}
              />
              <input
                className="border rounded-lg px-3 py-2 text-sm"
                placeholder="N.º póliza / certificado"
                value={sctrMasivo.numero}
                onChange={(e) => setSctrMasivo({ ...sctrMasivo, numero: e.target.value })}
              />
              <input
                type="date"
                className="border rounded-lg px-3 py-2 text-sm"
                title="Inicio"
                value={sctrMasivo.fecha_inicio}
                onChange={(e) => setSctrMasivo({ ...sctrMasivo, fecha_inicio: e.target.value })}
              />
              <input
                type="date"
                required
                className="border rounded-lg px-3 py-2 text-sm sm:col-span-2"
                title="Vencimiento"
                value={sctrMasivo.fecha_vencimiento}
                onChange={(e) => setSctrMasivo({ ...sctrMasivo, fecha_vencimiento: e.target.value })}
              />
              <CampoArchivo
                archivo={sctrMasivo.archivo}
                onChange={(archivo) => setSctrMasivo({ ...sctrMasivo, archivo })}
                etiqueta="Documento SCTR (PDF, JPG o PNG)"
              />
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex flex-col md:flex-row md:items-center gap-3">
                <div className="inline-flex rounded-full border border-gray-200 bg-white p-1">
                  {['Activo', 'Cesado'].map((estado) => {
                    const cantidad = estado === 'Activo' ? cantidadActivo : cantidadCesado;
                    const activo = filtroPersonal === estado;
                    return (
                      <button
                        key={estado}
                        type="button"
                        onClick={() => setFiltroPersonal(estado)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                          activo ? 'bg-teal-600 text-white' : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {estado === 'Activo' ? 'Personal activo' : 'Personal cesado'} ({cantidad})
                      </button>
                    );
                  })}
                </div>
                <input
                  type="search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Buscar por nombre, DNI, cargo o proyecto"
                  className="w-full md:flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'renovar', label: `Renovar (${conteoSctr.sin + conteoSctr.vencido})` },
                  { id: 'sin', label: `Sin SCTR (${conteoSctr.sin})` },
                  { id: 'vencido', label: `SCTR vencido (${conteoSctr.vencido})` },
                  { id: 'todos', label: 'Todos' },
                ].map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setFiltroSctr(item.id)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border ${
                      filtroSctr === item.id
                        ? 'bg-teal-600 text-white border-teal-600'
                        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <div className={`grid grid-cols-1 sm:grid-cols-2 ${planillaInternaActiva ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-3`}>
                <select
                  value={filtroProyecto}
                  onChange={(e) => setFiltroProyecto(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                >
                  <option value="">Todos los proyectos</option>
                  {proyectosUnicos.map((proyecto) => (
                    <option key={proyecto} value={proyecto}>{proyecto}</option>
                  ))}
                </select>
                <select
                  value={filtroArea}
                  onChange={(e) => setFiltroArea(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                >
                  <option value="">Todas las áreas</option>
                  {areasUnicas.map((area) => (
                    <option key={area} value={area}>{area}</option>
                  ))}
                </select>
                {planillaInternaActiva && (
                  <select
                    value={filtroTipoPlanilla}
                    onChange={(e) => setFiltroTipoPlanilla(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
                  >
                    <option value="">Todas las modalidades</option>
                    <option value="dual">Dual (PLAME + interna)</option>
                    <option value="interno">Solo interna</option>
                  </select>
                )}
              </div>
            </div>

            <div>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <p className="text-sm font-medium text-gray-800">
                  Trabajadores ({sctrMasivoIds.length} seleccionados · {fichasSctrVisibles.length} visibles)
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={seleccionarFiltradosSctrMasivo}
                    className="px-2.5 py-1 rounded-lg text-xs font-medium border border-teal-200 text-teal-800 bg-teal-50 hover:bg-teal-100"
                  >
                    Seleccionar todos los filtrados
                  </button>
                  <button
                    type="button"
                    onClick={() => setSctrMasivoIds([])}
                    className="px-2.5 py-1 rounded-lg text-xs font-medium border border-gray-200 text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Quitar selección
                  </button>
                </div>
              </div>
              <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-xl">
                {gruposSctr.map((grupo) => (
                  <div key={grupo.id}>
                    <div className="sticky top-0 z-10 flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">{grupo.titulo}</p>
                      <span className="text-xs text-gray-500">{grupo.items.length}</span>
                    </div>
                    {grupo.items.length === 0 && (
                      <p className="px-3 py-2 text-sm text-gray-400">Nadie en esta sección.</p>
                    )}
                    <div className="divide-y divide-gray-100">
                      {grupo.items.map((ficha) => {
                        const marcado = sctrMasivoIds.includes(String(ficha.id));
                        const estadoSctr = estadoSctrFicha(ficha);
                        return (
                          <label key={ficha.id} className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={marcado}
                              onChange={() => toggleSctrMasivoId(ficha.id)}
                            />
                            <span className="min-w-0 flex-1 truncate font-medium text-gray-800">{ficha.nombre_completo}</span>
                            <span className="text-xs text-gray-400 shrink-0">{ficha.numero_documento}</span>
                            <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold ${badgeEstado(estadoSctr)}`}>
                              {ficha.sctr?.fecha_vencimiento
                                ? `${labelEstado(estadoSctr)} · ${fmtFecha(ficha.sctr.fecha_vencimiento)}`
                                : 'Sin SCTR'}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
                {fichasSctrVisibles.length === 0 && (
                  <p className="px-3 py-3 text-sm text-gray-500">No hay trabajadores en el filtro actual.</p>
                )}
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={guardandoSctrMasivo}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-60"
              >
                {guardandoSctrMasivo ? 'Cargando…' : 'Cargar SCTR'}
              </button>
            </div>
          </form>
        </SeccionCard>
      )}

      {tab === 'accidentes' && (
        <SeccionCard titulo="Accidentes / incidentes">
          <p className="text-sm text-gray-600 mb-4">
            Registra el caso y abre su expediente. Luego carga los documentos hasta cerrarlo.
          </p>
          <form onSubmit={onTabAcc} className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
            <input
              type="datetime-local"
              required
              className="border rounded-lg px-3 py-2 text-sm"
              value={formAcc.fecha_hora}
              onChange={(e) => setFormAcc({ ...formAcc, fecha_hora: e.target.value })}
            />
            <select
              className="border rounded-lg px-3 py-2 text-sm"
              value={formAcc.tipo}
              onChange={(e) => setFormAcc({ ...formAcc, tipo: e.target.value })}
            >
              <option value="accidente">Accidente</option>
              <option value="incidente">Incidente</option>
              <option value="casi_accidente">Casi accidente</option>
            </select>
            <select
              className="border rounded-lg px-3 py-2 text-sm"
              value={formAcc.gravedad}
              onChange={(e) => setFormAcc({ ...formAcc, gravedad: e.target.value })}
            >
              <option value="leve">Leve</option>
              <option value="moderada">Moderada</option>
              <option value="grave">Grave</option>
              <option value="fatal">Fatal</option>
            </select>
            <input
              className="border rounded-lg px-3 py-2 text-sm"
              placeholder="Lugar"
              value={formAcc.lugar}
              onChange={(e) => setFormAcc({ ...formAcc, lugar: e.target.value })}
            />
            <SelectorInvolucrados
              fichas={fichas}
              seleccionados={formAcc.involucrados}
              busqueda={formAcc.busqueda}
              onBusqueda={(busqueda) => setFormAcc({ ...formAcc, busqueda })}
              onToggle={(id) => {
                const ya = formAcc.involucrados.includes(String(id));
                setFormAcc({
                  ...formAcc,
                  involucrados: ya
                    ? formAcc.involucrados.filter((x) => x !== String(id))
                    : [...formAcc.involucrados, String(id)],
                });
              }}
              onQuitar={(id) => setFormAcc({
                ...formAcc,
                involucrados: formAcc.involucrados.filter((x) => x !== String(id)),
              })}
              ayuda="Busca y marca a los trabajadores involucrados."
            />
            <textarea
              className="border rounded-lg px-3 py-2 text-sm md:col-span-2"
              rows={2}
              placeholder="Descripción"
              value={formAcc.descripcion}
              onChange={(e) => setFormAcc({ ...formAcc, descripcion: e.target.value })}
            />
            <button
              disabled={guardandoFicha === 'accidente-tab'}
              className="md:col-span-2 bg-teal-600 text-white rounded-lg px-3 py-2 text-sm disabled:opacity-60"
            >
              {guardandoFicha === 'accidente-tab' ? 'Abriendo…' : 'Abrir accidente'}
            </button>
          </form>
          <div className="inline-flex rounded-full border border-gray-200 bg-white p-1 mb-3">
            {[
              { id: 'todos', label: 'Todos' },
              { id: 'abiertos', label: 'Abiertos' },
              { id: 'cerrado', label: 'Cerrados' },
            ].map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setFiltroEstadoAcc(item.id)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                  filtroEstadoAcc === item.id ? 'bg-teal-600 text-white' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
          <ListaSimple
            items={accidentesVisibles}
            vacio={filtroEstadoAcc === 'cerrado' ? 'No hay accidentes cerrados.' : filtroEstadoAcc === 'abiertos' ? 'No hay accidentes abiertos.' : 'Sin accidentes registrados.'}
            render={(a) => (
              <button
                type="button"
                onClick={() => abrirPestanaAccidente(a)}
                className={`w-full text-left rounded-lg px-3 py-2 border ${
                  a.estado === 'cerrado'
                    ? 'border-slate-200 bg-slate-50 hover:bg-slate-100'
                    : 'border-gray-100 hover:border-rose-200 hover:bg-rose-50'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800">
                      {labelTipoAccidente(a.tipo)} · {a.gravedad || 'Sin gravedad'} · {fmtFecha(a.fecha_hora)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {(a.empleados || []).map((e) => e.nombre_completo).join(', ') || 'Sin involucrados'}
                      {(a.documentos || []).length ? ` · ${(a.documentos || []).length} documento(s)` : ''}
                    </p>
                  </div>
                  <BadgeEstadoAccidente estado={a.estado} />
                </div>
              </button>
            )}
          />
        </SeccionCard>
      )}

      {tab === 'catalogo-epp' && (
        <SeccionCard titulo="Catálogo de EPP">
          <p className="text-sm text-gray-600 mb-4">
            Carga aquí los EPP de la empresa (casco, chaleco, botas, lentes, etc.). Luego, en cada ficha,
            solo marcas cuáles se entregan y se genera la constancia.
          </p>
          <form onSubmit={onGuardarCatalogoEpp} className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
            <input
              required
              className="border rounded-lg px-3 py-2 text-sm md:col-span-2"
              placeholder="Nombre del EPP (ej. Casco, Chaleco, Botas de seguridad)"
              value={formCatalogoEpp.nombre}
              onChange={(e) => setFormCatalogoEpp({ ...formCatalogoEpp, nombre: e.target.value })}
            />
            <input
              type="number"
              min="1"
              className="border rounded-lg px-3 py-2 text-sm"
              placeholder="Vida útil (días, opcional)"
              value={formCatalogoEpp.vida_util_dias}
              onChange={(e) => setFormCatalogoEpp({ ...formCatalogoEpp, vida_util_dias: e.target.value })}
            />
            <button
              disabled={guardandoCatalogo}
              className="md:col-span-3 bg-teal-600 text-white rounded-lg px-3 py-2 text-sm disabled:opacity-60"
            >
              {guardandoCatalogo
                ? 'Guardando…'
                : edicion.tipo === 'catalogo-epp'
                  ? 'Guardar cambios'
                  : 'Agregar al catálogo'}
            </button>
            {edicion.tipo === 'catalogo-epp' && (
              <button type="button" onClick={cancelarEdicion} className="md:col-span-3 text-sm text-gray-600">
                Cancelar edición
              </button>
            )}
          </form>
          {(eppData.catalogo || []).length === 0 ? (
            <p className="text-sm text-gray-500">Aún no hay EPP en el catálogo.</p>
          ) : (
            <div className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
              {(eppData.catalogo || []).map((item) => {
                const usado = eppCatalogoUsado(item);
                const usos = Number(item.movimientos_count || 0);
                const editando = edicion.tipo === 'catalogo-epp' && Number(edicion.id) === Number(item.id);
                return (
                  <div key={item.id} className={`flex items-center justify-between gap-3 px-4 py-3 ${editando ? 'bg-teal-50' : 'bg-white'}`}>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{item.nombre}</p>
                      <p className="text-xs text-gray-500">
                        {item.vida_util_dias ? `Vida útil: ${item.vida_util_dias} días` : 'Sin vida útil definida'}
                        {usado ? ` · Usado en ${usos} registro${usos === 1 ? '' : 's'}` : ' · Sin usar'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => empezarEditarCatalogoEpp(item)}
                        className="text-xs text-teal-800 border border-teal-200 bg-white rounded px-2 py-1 hover:bg-teal-50"
                      >
                        Editar
                      </button>
                      {usado ? (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                          En uso
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => borrarCatalogoEpp(item)}
                          className="text-xs text-red-700 border border-red-200 bg-white rounded px-2 py-1 hover:bg-red-50"
                        >
                          Borrar
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </SeccionCard>
      )}

      {tab === 'inspecciones' && (
        <SeccionCard titulo="Inspecciones">
          <form onSubmit={onGuardarInspeccion} className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
            <input required type="date" className="border rounded-lg px-3 py-2 text-sm" value={formIns.fecha} onChange={(e) => setFormIns({ ...formIns, fecha: e.target.value })} />
            <select className="border rounded-lg px-3 py-2 text-sm" value={formIns.tipo} onChange={(e) => setFormIns({ ...formIns, tipo: e.target.value })}>
              <option value="planeada">Planeada</option>
              <option value="inopinada">Inopinada</option>
            </select>
            <select
              required={areasCatalogo.length > 0}
              className="border rounded-lg px-3 py-2 text-sm bg-white"
              value={formIns.area}
              onChange={(e) => setFormIns({ ...formIns, area: e.target.value })}
            >
              <option value="">{areasCatalogo.length ? 'Seleccionar área' : 'Sin áreas configuradas'}</option>
              {areasCatalogo.map((area) => (
                <option key={area.id || area.nombre} value={area.nombre}>{area.nombre}</option>
              ))}
            </select>
            <input className="border rounded-lg px-3 py-2 text-sm" placeholder="Inspector" value={formIns.inspector} onChange={(e) => setFormIns({ ...formIns, inspector: e.target.value })} />
            <input className="border rounded-lg px-3 py-2 text-sm md:col-span-2" placeholder="Hallazgo" value={formIns.hallazgo} onChange={(e) => setFormIns({ ...formIns, hallazgo: e.target.value })} />
            <CampoArchivo archivo={formIns.archivo} onChange={(archivo) => setFormIns({ ...formIns, archivo })} etiqueta="Escaneo de la inspección (PDF, JPG o PNG)" />
            <button disabled={guardandoFicha === 'inspeccion'} className="md:col-span-2 bg-teal-600 text-white rounded-lg px-3 py-2 text-sm disabled:opacity-60">
              {guardandoFicha === 'inspeccion' ? 'Guardando…' : edicion.tipo === 'inspeccion' ? 'Guardar cambios del expediente' : 'Guardar nuevo expediente'}
            </button>
            {edicion.tipo === 'inspeccion' && (
              <button type="button" onClick={cancelarEdicion} className="md:col-span-2 text-sm text-gray-600">Cancelar edición</button>
            )}
          </form>
          <div className="space-y-2">
            {inspecciones.length === 0 && <p className="text-sm text-gray-500">Sin inspecciones.</p>}
            {inspecciones.map((i) => (
              <TarjetaExpediente
                key={i.id}
                titulo={`${fmtFecha(i.fecha)} · ${i.tipo === 'inopinada' ? 'Inopinada' : 'Planeada'}${i.area ? ` · ${i.area}` : ''}`}
                detalle={`${i.inspector ? `${i.inspector} · ` : ''}${i.hallazgos?.length || 0} hallazgo(s)${i.hallazgos?.[0]?.descripcion ? ` · ${i.hallazgos[0].descripcion}` : ''}`}
                documentos={i.documentos || []}
                empresaId={empresaId}
                editando={edicion.tipo === 'inspeccion' && Number(edicion.id) === Number(i.id)}
                onEditar={() => empezarEditarInspeccion(i)}
                onBorrar={() => borrarExpediente('inspeccion', i.id)}
                onBorrarArchivo={(docId) => borrarExpediente('otro', docId, true)}
              />
            ))}
          </div>
        </SeccionCard>
      )}

      {tab === 'iper' && (
        <SeccionCard titulo="IPER / IPERC">
          <form onSubmit={onGuardarIper} className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
            <input required className="border rounded-lg px-3 py-2 text-sm" placeholder="Nombre de la matriz" value={formIper.nombre} onChange={(e) => setFormIper({ ...formIper, nombre: e.target.value })} />
            <input className="border rounded-lg px-3 py-2 text-sm" placeholder="Versión" value={formIper.version} onChange={(e) => setFormIper({ ...formIper, version: e.target.value })} />
            <input className="border rounded-lg px-3 py-2 text-sm" placeholder="Actividad" value={formIper.actividad} onChange={(e) => setFormIper({ ...formIper, actividad: e.target.value })} />
            <input className="border rounded-lg px-3 py-2 text-sm" placeholder="Peligro / riesgo" value={formIper.peligro} onChange={(e) => setFormIper({ ...formIper, peligro: e.target.value, riesgo: e.target.value })} />
            <CampoArchivo archivo={formIper.archivo} onChange={(archivo) => setFormIper({ ...formIper, archivo })} etiqueta="Escaneo de la matriz (PDF, JPG o PNG)" />
            <button disabled={guardandoFicha === 'iper'} className="md:col-span-2 bg-teal-600 text-white rounded-lg px-3 py-2 text-sm disabled:opacity-60">
              {guardandoFicha === 'iper' ? 'Guardando…' : edicion.tipo === 'iper' ? 'Guardar cambios del expediente' : 'Guardar nuevo expediente'}
            </button>
            {edicion.tipo === 'iper' && (
              <button type="button" onClick={cancelarEdicion} className="md:col-span-2 text-sm text-gray-600">Cancelar edición</button>
            )}
          </form>
          <div className="space-y-2">
            {iper.length === 0 && <p className="text-sm text-gray-500">Sin matrices.</p>}
            {iper.map((m) => (
              <TarjetaExpediente
                key={m.id}
                titulo={m.nombre}
                detalle={`v${m.version || '—'} · ${m.filas?.length || 0} fila(s)${m.filas?.[0]?.actividad ? ` · ${m.filas[0].actividad}` : ''}`}
                documentos={m.documentos || []}
                empresaId={empresaId}
                editando={edicion.tipo === 'iper' && Number(edicion.id) === Number(m.id)}
                onEditar={() => empezarEditarIper(m)}
                onBorrar={() => borrarExpediente('iper', m.id)}
                onBorrarArchivo={(docId) => borrarExpediente('otro', docId, true)}
              />
            ))}
          </div>
        </SeccionCard>
      )}

      {String(tab || '').startsWith('accidente-') && (
        <SeccionCard titulo={detalleAccidente ? etiquetaPestanaAccidente(detalleAccidente) : 'Expediente del accidente'}>
          {cargandoAccidente && !detalleAccidente ? (
            <p className="text-sm text-gray-500">Cargando expediente...</p>
          ) : !detalleAccidente ? (
            <p className="text-sm text-gray-500">No se encontró el accidente.</p>
          ) : (
            <div className="space-y-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                      detalleAccidente.estado === 'cerrado' ? 'bg-gray-200 text-gray-700' : 'bg-rose-100 text-rose-800'
                    }`}>
                      {labelEstadoAccidente(detalleAccidente.estado)}
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-700">
                      {detalleAccidente.gravedad || 'Sin gravedad'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {fmtFecha(detalleAccidente.fecha_hora)} · {detalleAccidente.lugar || 'Sin lugar'}
                  </p>
                  {detalleAccidente.descripcion && (
                    <p className="text-sm text-gray-700 mt-2">{detalleAccidente.descripcion}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    Involucrados: {(detalleAccidente.empleados || []).map((e) => e.nombre_completo).join(', ') || '—'}
                  </p>
                </div>
                {detalleAccidente.estado !== 'cerrado' && (
                  <button
                    type="button"
                    onClick={onCerrarAccidente}
                    disabled={cerrandoAccidente}
                    className="px-3 py-2 rounded-lg text-sm font-medium bg-gray-800 text-white disabled:opacity-60"
                  >
                    {cerrandoAccidente ? 'Cerrando…' : 'Cerrar accidente'}
                  </button>
                )}
              </div>

              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Documentos del caso</h4>
                {(detalleAccidente.documentos || []).length === 0 && (
                  <p className="text-sm text-gray-500 mb-3">Aún no hay documentos en este accidente.</p>
                )}
                <div className="space-y-2">
                  {(detalleAccidente.documentos || []).map((d) => (
                    <div key={d.id} className="flex items-center justify-between gap-3 border border-gray-100 rounded-lg px-3 py-2">
                      <div className="min-w-0">
                        <button
                          type="button"
                          className="text-sm text-teal-700 underline text-left truncate block"
                          onClick={() => ssomaService.descargarDocumento(empresaId, d.id, d.archivo_nombre)}
                        >
                          {d.titulo || d.archivo_nombre}
                        </button>
                        <p className="text-xs text-gray-500 truncate">
                          {labelInvolucradosDocumento(d, detalleAccidente.empleados || [])}
                        </p>
                      </div>
                      {detalleAccidente.estado !== 'cerrado' && (
                        <button
                          type="button"
                          onClick={() => onQuitarDocAccidente(d.id)}
                          className="text-xs text-red-600 hover:text-red-800 shrink-0"
                        >
                          Quitar
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {detalleAccidente.estado === 'cerrado' ? (
                <p className="text-sm text-gray-500">El accidente está cerrado. Solo se puede consultar el expediente.</p>
              ) : (
                <form onSubmit={onAgregarDocsAccidente} className="grid grid-cols-1 md:grid-cols-2 gap-3 border border-dashed border-rose-200 rounded-xl p-4 bg-rose-50/40">
                  <p className="md:col-span-2 text-sm text-rose-800">
                    Elige a quién corresponde el documento: uno, varios o todos los involucrados.
                  </p>
                  <div className="md:col-span-2 border border-rose-100 rounded-lg p-3 bg-white space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-medium text-gray-700">Corresponde a</p>
                      {(detalleAccidente.empleados || []).length > 1 && (
                        <button
                          type="button"
                          onClick={() => setDocAccidente({
                            ...docAccidente,
                            involucrados: (detalleAccidente.empleados || []).map((e) => String(e.id)),
                          })}
                          className="text-xs text-rose-700 hover:underline"
                        >
                          Marcar todos
                        </button>
                      )}
                    </div>
                    {(detalleAccidente.empleados || []).map((empleado) => {
                      const id = String(empleado.id);
                      const marcado = (docAccidente.involucrados || []).includes(id);
                      return (
                        <label key={empleado.id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={marcado}
                            onChange={() => {
                              const actuales = docAccidente.involucrados || [];
                              setDocAccidente({
                                ...docAccidente,
                                involucrados: marcado
                                  ? actuales.filter((x) => x !== id)
                                  : [...actuales, id],
                              });
                            }}
                          />
                          <span>{empleado.nombre_completo}</span>
                          {empleado.numero_documento && (
                            <span className="text-xs text-gray-400">{empleado.numero_documento}</span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                  <input
                    className="border rounded-lg px-3 py-2 text-sm md:col-span-2"
                    placeholder="Título (opcional, se usa para todos los archivos de esta carga)"
                    value={docAccidente.titulo}
                    onChange={(e) => setDocAccidente({ ...docAccidente, titulo: e.target.value })}
                  />
                  <label className="md:col-span-2 block border border-dashed border-gray-300 rounded-lg px-3 py-2 bg-white">
                    <span className="block text-xs text-gray-600 mb-1">PDF, JPG o PNG (varios a la vez)</span>
                    <input
                      key={`${detalleAccidente.id}-${(detalleAccidente.documentos || []).length}`}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      multiple
                      className="text-xs w-full"
                      onChange={(e) => setDocAccidente({ ...docAccidente, archivos: Array.from(e.target.files || []) })}
                    />
                    {docAccidente.archivos?.length > 0 && (
                      <span className="block text-xs text-teal-700 mt-1">
                        {docAccidente.archivos.length} archivo(s) seleccionado(s)
                      </span>
                    )}
                  </label>
                  <button
                    disabled={guardandoFicha === 'docs-accidente'}
                    className="md:col-span-2 bg-rose-600 text-white rounded-lg px-3 py-2 text-sm disabled:opacity-60"
                  >
                    {guardandoFicha === 'docs-accidente' ? 'Subiendo…' : 'Agregar documentos'}
                  </button>
                </form>
              )}
            </div>
          )}
        </SeccionCard>
      )}

      {fichaAbierta && (
        <div className="fixed inset-0 z-40 bg-black/40 flex justify-end" onClick={() => setFichaAbierta(null)}>
          <div className="w-full max-w-2xl h-full bg-white shadow-xl overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-start gap-3 min-w-0">
                {fotosPersonalActivo && detalle?.empleado?.foto_carnet_url && (
                  <img
                    src={empleadoService.urlFotoCarnet(detalle.empleado.foto_carnet_url)}
                    alt={detalle.empleado.nombre_completo}
                    className="h-12 w-12 rounded-full object-cover shrink-0"
                  />
                )}
                <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-xl font-semibold text-gray-900">{detalle?.empleado?.nombre_completo || 'Ficha SSOMA'}</h3>
                  {String(detalle?.empleado?.estado || '').toLowerCase() === 'cesado' && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-800">Cesado</span>
                  )}
                </div>
                <p className="text-sm text-gray-500">
                  {detalle?.empleado?.numero_documento} · {detalle?.empleado?.cargo} · {detalle?.empleado?.proyecto || 'Sin proyecto'} · {detalle?.empleado?.areas_de_trabajo || 'Sin área'}
                </p>
                {String(detalle?.empleado?.estado || '').toLowerCase() === 'cesado' ? (
                  <p className="text-xs text-red-700 mt-1">Personal cesado. Puedes consultar su historial SSOMA.</p>
                ) : (
                  <p className="text-xs text-teal-700 mt-1">Desde esta ficha se registran los datos y el escaneo de cada sección.</p>
                )}
                </div>
              </div>
              <button type="button" onClick={() => setFichaAbierta(null)} className="text-gray-500 hover:text-gray-800">Cerrar</button>
            </div>
            {cargandoFicha ? (
              <p className="text-sm text-gray-500">Cargando resumen...</p>
            ) : detalle && (
              <div className="space-y-5 text-sm">
                <Bloque titulo="Tallas (pantalón, polo y zapatos)">
                  <SsomaTallasTab
                    empresaId={empresaId}
                    empleadoId={fichaAbierta}
                    compacto
                    soloLectura
                  />
                </Bloque>
                <Bloque titulo="SCTR">
                  {(detalle.sctr || []).length === 0 && <p className="text-gray-500 mb-2">Aún no hay expedientes SCTR.</p>}
                  <div className="space-y-2 mb-3">
                    {(detalle.sctr || []).map((s, idx) => (
                      <TarjetaExpediente
                        key={s.id}
                        titulo={`SCTR N.º ${idx + 1}`}
                        detalle={`${s.tipo === 'ambos' ? 'Pensión y salud' : s.tipo} · ${s.aseguradora || 'Sin aseguradora'}${s.numero ? ` · ${s.numero}` : ''} · vence ${fmtFecha(s.fecha_vencimiento)} · ${labelEstado(s.estado)}`}
                        documentos={s.documentos || []}
                        empresaId={empresaId}
                        editando={edicion.tipo === 'sctr' && Number(edicion.id) === Number(s.id)}
                        onEditar={() => empezarEditarSctr(s)}
                        onBorrar={() => borrarExpediente('sctr', s.id)}
                        onBorrarArchivo={(docId) => borrarExpediente('otro', docId, true)}
                      />
                    ))}
                  </div>
                  <form onSubmit={onFichaSctr} className="mt-3 grid grid-cols-2 gap-2">
                    <select className="border rounded px-2 py-1.5" value={fichaSctr.tipo} onChange={(e) => setFichaSctr({ ...fichaSctr, tipo: e.target.value })}>
                      <option value="ambos">Pensión y salud</option>
                      <option value="pension">Pensión</option>
                      <option value="salud">Salud</option>
                    </select>
                    <input className="border rounded px-2 py-1.5" placeholder="Aseguradora" value={fichaSctr.aseguradora} onChange={(e) => setFichaSctr({ ...fichaSctr, aseguradora: e.target.value })} />
                    <input className="border rounded px-2 py-1.5" placeholder="N.º póliza / certificado" value={fichaSctr.numero} onChange={(e) => setFichaSctr({ ...fichaSctr, numero: e.target.value })} />
                    <input type="date" className="border rounded px-2 py-1.5" title="Inicio" value={fichaSctr.fecha_inicio} onChange={(e) => setFichaSctr({ ...fichaSctr, fecha_inicio: e.target.value })} />
                    <input type="date" required className="border rounded px-2 py-1.5 col-span-2" title="Vencimiento" value={fichaSctr.fecha_vencimiento} onChange={(e) => setFichaSctr({ ...fichaSctr, fecha_vencimiento: e.target.value })} />
                    <CampoArchivo archivo={fichaSctr.archivo} onChange={(archivo) => setFichaSctr({ ...fichaSctr, archivo })} />
                    <button disabled={guardandoFicha === 'sctr'} className="col-span-2 bg-teal-600 text-white rounded px-3 py-2 disabled:opacity-60">
                      {guardandoFicha === 'sctr' ? 'Guardando…' : edicion.tipo === 'sctr' ? 'Guardar cambios del expediente' : 'Guardar nuevo expediente SCTR'}
                    </button>
                    {edicion.tipo === 'sctr' && (
                      <button type="button" onClick={cancelarEdicion} className="col-span-2 text-sm text-gray-600">Cancelar edición</button>
                    )}
                  </form>
                </Bloque>

                <Bloque titulo="EMO (examen médico ocupacional)">
                  {(detalle.emos || []).length === 0 && <p className="text-gray-500 mb-2">Aún no hay expedientes EMO.</p>}
                  <div className="space-y-2 mb-3">
                    {(detalle.emos || []).map((e, idx) => (
                      <TarjetaExpediente
                        key={e.id}
                        titulo={`EMO N.º ${idx + 1} · ${labelTipoEmo(e.tipo)}`}
                        detalle={`${labelAptitudEmo(e.aptitud)} · ${e.clinica || 'Sin clínica'} · vence ${fmtFecha(e.fecha_vencimiento)} · ${labelEstado(e.estado)}`}
                        documentos={e.documentos || []}
                        empresaId={empresaId}
                        editando={edicion.tipo === 'emo' && Number(edicion.id) === Number(e.id)}
                        onEditar={() => empezarEditarEmo(e)}
                        onBorrar={() => borrarExpediente('emo', e.id)}
                        onBorrarArchivo={(docId) => borrarExpediente('otro', docId, true)}
                      />
                    ))}
                  </div>
                  <form onSubmit={onFichaEmo} className="mt-3 grid grid-cols-2 gap-2">
                    <select className="border rounded px-2 py-1.5" value={fichaEmo.tipo} onChange={(ev) => setFichaEmo({ ...fichaEmo, tipo: ev.target.value })}>
                      <option value="periodico">Periódico</option>
                      <option value="preocupacional">Preocupacional</option>
                      <option value="retiro">Retiro</option>
                      <option value="cambio_puesto">Cambio de puesto</option>
                      <option value="reincorporacion">Reincorporación</option>
                    </select>
                    <select className="border rounded px-2 py-1.5" value={fichaEmo.aptitud} onChange={(ev) => setFichaEmo({ ...fichaEmo, aptitud: ev.target.value })}>
                      <option value="apto">Apto</option>
                      <option value="apto_con_restricciones">Apto con restricciones</option>
                      <option value="no_apto">No apto</option>
                    </select>
                    <input className="border rounded px-2 py-1.5" placeholder="Clínica / centro médico" value={fichaEmo.clinica} onChange={(ev) => setFichaEmo({ ...fichaEmo, clinica: ev.target.value })} />
                    <input type="date" className="border rounded px-2 py-1.5" title="Fecha de examen" value={fichaEmo.fecha_examen} onChange={(ev) => setFichaEmo({ ...fichaEmo, fecha_examen: ev.target.value })} />
                    <input type="date" required className="border rounded px-2 py-1.5 col-span-2" title="Vencimiento" value={fichaEmo.fecha_vencimiento} onChange={(ev) => setFichaEmo({ ...fichaEmo, fecha_vencimiento: ev.target.value })} />
                    <CampoArchivo archivo={fichaEmo.archivo} onChange={(archivo) => setFichaEmo({ ...fichaEmo, archivo })} etiqueta="EMO escaneado (PDF, JPG o PNG)" />
                    <button disabled={guardandoFicha === 'emo'} className="col-span-2 bg-teal-600 text-white rounded px-3 py-2 disabled:opacity-60">
                      {guardandoFicha === 'emo' ? 'Guardando…' : edicion.tipo === 'emo' ? 'Guardar cambios del expediente' : 'Guardar nuevo expediente EMO'}
                    </button>
                    {edicion.tipo === 'emo' && (
                      <button type="button" onClick={cancelarEdicion} className="col-span-2 text-sm text-gray-600">Cancelar edición</button>
                    )}
                  </form>
                </Bloque>

                <Bloque titulo="EPP">
                  {(detalle.epp_registros || []).length === 0 && (detalle.epp || []).length === 0 && (
                    <p className="text-gray-500 mb-2">Aún no hay registros en el expediente.</p>
                  )}
                  <div className="space-y-2 mb-3">
                    {(detalle.epp_registros || []).map((registro) => {
                      const items = registro.movimientos || [];
                      return (
                        <TarjetaExpediente
                          key={registro.id}
                          titulo={`${labelTipoEpp(registro.tipo)} N.º ${registro.numero}`}
                          detalle={`${fmtFecha(registro.fecha)} · ${items.length} EPP`}
                          documentos={registro.documentos || []}
                          empresaId={empresaId}
                          editando={edicion.tipo === 'epp' && Number(edicion.id) === Number(registro.id)}
                          onEditar={() => empezarEditarEpp(registro)}
                          onBorrar={() => borrarExpediente('epp', registro.id)}
                          onBorrarArchivo={(docId) => borrarExpediente('otro', docId, true)}
                          extra={(
                            <>
                              <ul className="mt-1 space-y-0.5">
                                {items.map((m) => (
                                  <li key={m.id} className="text-xs text-gray-600">
                                    {m.catalogo?.nombre || 'EPP'}
                                    {m.cantidad > 1 ? ` × ${m.cantidad}` : ''}
                                    {m.fecha_vencimiento ? ` · vence ${fmtFecha(m.fecha_vencimiento)}` : ''}
                                  </li>
                                ))}
                              </ul>
                              <button
                                type="button"
                                onClick={() => descargarConstanciaEpp(registro)}
                                disabled={guardandoFicha === `constancia-${registro.id}`}
                                className="mt-2 text-xs text-teal-800 border border-teal-200 bg-white rounded px-2 py-1 hover:bg-teal-50 disabled:opacity-60"
                              >
                                {guardandoFicha === `constancia-${registro.id}` ? 'Preparando…' : 'Descargar constancia'}
                              </button>
                            </>
                          )}
                        />
                      );
                    })}
                  </div>
                  <form onSubmit={onFichaEpp} className="mt-3 space-y-3">
                    <p className="text-xs text-gray-500">
                      Cada registro se guarda en el expediente y no reemplaza a los anteriores.
                    </p>
                    <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                      {(eppData.catalogo || []).length === 0 && (
                        <p className="px-3 py-2 text-xs text-gray-500">Aún no hay catálogo. Agrega un EPP abajo.</p>
                      )}
                      {(eppData.catalogo || []).map((c) => {
                        const marcado = fichaEpp.items.some((epp) => String(epp.catalogo_id) === String(c.id));
                        return (
                          <label key={c.id} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50">
                            <input
                              type="checkbox"
                              checked={marcado}
                              onChange={() => toggleEppCatalogo(c)}
                            />
                            <span>{c.nombre}{c.talla ? ` (${c.talla})` : ''}</span>
                          </label>
                        );
                      })}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        className="border rounded px-2 py-1.5"
                        placeholder="Agregar EPP que no está en la lista"
                        value={fichaEpp.nuevo}
                        onChange={(e) => setFichaEpp({ ...fichaEpp, nuevo: e.target.value })}
                      />
                      <button
                        type="button"
                        onClick={agregarEppNuevo}
                        className="border border-teal-200 text-teal-800 rounded px-2 py-1.5 text-sm hover:bg-teal-50"
                      >
                        Agregar a la entrega
                      </button>
                      <select className="border rounded px-2 py-1.5" value={fichaEpp.tipo} onChange={(e) => setFichaEpp({ ...fichaEpp, tipo: e.target.value })}>
                        <option value="entrega">Entrega</option>
                        <option value="devolucion">Devolución</option>
                        <option value="reposicion">Reposición</option>
                      </select>
                      <input type="date" required className="border rounded px-2 py-1.5" value={fichaEpp.fecha} onChange={(e) => setFichaEpp({ ...fichaEpp, fecha: e.target.value })} />
                      <input type="date" className="border rounded px-2 py-1.5 col-span-2" title="Vencimiento" value={fichaEpp.fecha_vencimiento} onChange={(e) => setFichaEpp({ ...fichaEpp, fecha_vencimiento: e.target.value })} />
                    </div>
                    {fichaEpp.items.length > 0 && (
                      <div className="border border-teal-100 rounded-lg bg-teal-50/40 p-2 space-y-1">
                        <p className="text-xs font-medium text-teal-900">Se incluirán en la constancia:</p>
                        {fichaEpp.items.map((item) => (
                          <div key={item.key} className="flex items-center gap-2 text-sm">
                            <span className="flex-1 truncate">{item.nombre}</span>
                            <input
                              type="number"
                              min="1"
                              className="w-16 border rounded px-1.5 py-1 text-sm"
                              value={item.cantidad}
                              onChange={(e) => setFichaEpp((prev) => ({
                                ...prev,
                                items: prev.items.map((epp) => epp.key === item.key
                                  ? { ...epp, cantidad: Math.max(1, Number(e.target.value) || 1) }
                                  : epp),
                              }))}
                            />
                            <button
                              type="button"
                              className="text-xs text-red-600"
                              onClick={() => setFichaEpp((prev) => ({
                                ...prev,
                                items: prev.items.filter((epp) => epp.key !== item.key),
                              }))}
                            >
                              Quitar
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <button disabled={guardandoFicha === 'epp'} className="w-full bg-teal-600 text-white rounded px-3 py-2 disabled:opacity-60">
                      {guardandoFicha === 'epp' ? 'Guardando registro…' : edicion.tipo === 'epp' ? 'Guardar cambios y constancia' : 'Guardar nuevo registro y constancia'}
                    </button>
                    {edicion.tipo === 'epp' && (
                      <button type="button" onClick={cancelarEdicion} className="w-full text-sm text-gray-600">Cancelar edición</button>
                    )}
                  </form>
                </Bloque>

                <Bloque titulo="Capacitaciones">
                  {(detalle.capacitaciones || []).length === 0 && <p className="text-gray-500 mb-2">Aún no hay expedientes de capacitación.</p>}
                  <div className="space-y-2 mb-3">
                    {(detalle.capacitaciones || []).map((c, idx) => (
                      <TarjetaExpediente
                        key={c.id}
                        titulo={`Capacitación N.º ${idx + 1}`}
                        detalle={`${c.sesion?.curso?.nombre || 'Curso'} · ${fmtFecha(c.sesion?.fecha)}${c.fecha_vencimiento ? ` · vence ${fmtFecha(c.fecha_vencimiento)}` : ''}`}
                        documentos={c.documentos || []}
                        empresaId={empresaId}
                        editando={edicion.tipo === 'curso' && Number(edicion.id) === Number(c.id)}
                        onEditar={() => empezarEditarCurso(c)}
                        onBorrar={() => borrarExpediente('curso', c.id)}
                        onBorrarArchivo={(docId) => borrarExpediente('otro', docId, true)}
                      />
                    ))}
                  </div>
                  <form onSubmit={onFichaCurso} className="mt-3 grid grid-cols-2 gap-2">
                    <select className="border rounded px-2 py-1.5" value={fichaCurso.curso_id} onChange={(e) => setFichaCurso({ ...fichaCurso, curso_id: e.target.value })}>
                      <option value="">Curso existente</option>
                      {cursos.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                    </select>
                    <input className="border rounded px-2 py-1.5" placeholder="O nombre de curso nuevo" value={fichaCurso.nuevo} onChange={(e) => setFichaCurso({ ...fichaCurso, nuevo: e.target.value })} />
                    <select className="border rounded px-2 py-1.5" value={fichaCurso.tipo} onChange={(e) => setFichaCurso({ ...fichaCurso, tipo: e.target.value })}>
                      <option value="induccion">Inducción</option>
                      <option value="capacitacion">Capacitación</option>
                      <option value="charla">Charla</option>
                    </select>
                    <input type="date" required className="border rounded px-2 py-1.5" value={fichaCurso.fecha} onChange={(e) => setFichaCurso({ ...fichaCurso, fecha: e.target.value })} />
                    <input type="date" className="border rounded px-2 py-1.5 col-span-2" title="Vencimiento" value={fichaCurso.fecha_vencimiento} onChange={(e) => setFichaCurso({ ...fichaCurso, fecha_vencimiento: e.target.value })} />
                    <CampoArchivo archivo={fichaCurso.archivo} onChange={(archivo) => setFichaCurso({ ...fichaCurso, archivo })} etiqueta="Lista / constancia escaneada" />
                    <button disabled={guardandoFicha === 'curso'} className="col-span-2 bg-teal-600 text-white rounded px-3 py-2 disabled:opacity-60">
                      {guardandoFicha === 'curso' ? 'Guardando…' : edicion.tipo === 'curso' ? 'Guardar cambios del expediente' : 'Guardar nuevo expediente'}
                    </button>
                    {edicion.tipo === 'curso' && (
                      <button type="button" onClick={cancelarEdicion} className="col-span-2 text-sm text-gray-600">Cancelar edición</button>
                    )}
                  </form>
                </Bloque>

                <Bloque titulo="Accidentes / incidentes">
                  {(detalle.accidentes || []).length === 0 && <p className="text-gray-500 mb-2">Sin incidentes.</p>}
                  <div className="space-y-2 mb-3">
                    {(detalle.accidentes || []).map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => abrirPestanaAccidente(a)}
                        className={`w-full text-left rounded-lg border px-3 py-2 ${
                          a.estado === 'cerrado'
                            ? 'border-slate-200 bg-slate-50 hover:bg-slate-100'
                            : 'border-gray-100 hover:border-rose-200 hover:bg-rose-50'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-medium text-gray-800">
                            {labelTipoAccidente(a.tipo)} · {a.gravedad || ''} · {fmtFecha(a.fecha_hora)}
                          </p>
                          <BadgeEstadoAccidente estado={a.estado} />
                        </div>
                        <p className="text-xs text-gray-500">
                          {(a.documentos || []).length} documento(s)
                          {(a.empleados || []).length > 1 ? ` · Involucrados: ${(a.empleados || []).map((e) => e.nombre_completo).join(', ')}` : ''}
                          {a.estado === 'cerrado' ? ' · Ver expediente' : ' · Abrir expediente'}
                        </p>
                      </button>
                    ))}
                  </div>
                  <form onSubmit={onFichaAcc} className="mt-3 grid grid-cols-2 gap-2">
                    <input type="datetime-local" required className="border rounded px-2 py-1.5" value={fichaAcc.fecha_hora} onChange={(e) => setFichaAcc({ ...fichaAcc, fecha_hora: e.target.value })} />
                    <select className="border rounded px-2 py-1.5" value={fichaAcc.tipo} onChange={(e) => setFichaAcc({ ...fichaAcc, tipo: e.target.value })}>
                      <option value="accidente">Accidente</option>
                      <option value="incidente">Incidente</option>
                      <option value="casi_accidente">Casi accidente</option>
                    </select>
                    <select className="border rounded px-2 py-1.5" value={fichaAcc.gravedad} onChange={(e) => setFichaAcc({ ...fichaAcc, gravedad: e.target.value })}>
                      <option value="leve">Leve</option>
                      <option value="moderada">Moderada</option>
                      <option value="grave">Grave</option>
                      <option value="fatal">Fatal</option>
                    </select>
                    <input className="border rounded px-2 py-1.5" placeholder="Lugar" value={fichaAcc.lugar} onChange={(e) => setFichaAcc({ ...fichaAcc, lugar: e.target.value })} />
                    <div className="col-span-2 grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setFichaAcc({ ...fichaAcc, alcance: 'personal', involucrados: [], busqueda: '' })}
                        className={`rounded-lg px-3 py-2 text-sm font-medium border ${
                          fichaAcc.alcance === 'personal'
                            ? 'bg-teal-600 text-white border-teal-600'
                            : 'bg-white text-gray-700 border-gray-200'
                        }`}
                      >
                        Personal
                      </button>
                      <button
                        type="button"
                        onClick={() => setFichaAcc({ ...fichaAcc, alcance: 'multiple' })}
                        className={`rounded-lg px-3 py-2 text-sm font-medium border ${
                          fichaAcc.alcance === 'multiple'
                            ? 'bg-teal-600 text-white border-teal-600'
                            : 'bg-white text-gray-700 border-gray-200'
                        }`}
                      >
                        Múltiple
                      </button>
                    </div>
                    {fichaAcc.alcance === 'personal' ? (
                      <p className="col-span-2 text-xs text-gray-500">Solo se registra a {detalle.empleado?.nombre_completo}.</p>
                    ) : (
                      <SelectorInvolucrados
                        fichas={fichas}
                        actualId={fichaAbierta}
                        seleccionados={fichaAcc.involucrados}
                        busqueda={fichaAcc.busqueda}
                        onBusqueda={(busqueda) => setFichaAcc({ ...fichaAcc, busqueda })}
                        onToggle={(id) => {
                          const ya = fichaAcc.involucrados.includes(String(id));
                          setFichaAcc({
                            ...fichaAcc,
                            involucrados: ya
                              ? fichaAcc.involucrados.filter((x) => x !== String(id))
                              : [...fichaAcc.involucrados, String(id)],
                          });
                        }}
                        onQuitar={(id) => setFichaAcc({
                          ...fichaAcc,
                          involucrados: fichaAcc.involucrados.filter((x) => x !== String(id)),
                        })}
                      />
                    )}
                    <textarea className="border rounded px-2 py-1.5 col-span-2" rows={2} placeholder="Descripción" value={fichaAcc.descripcion} onChange={(e) => setFichaAcc({ ...fichaAcc, descripcion: e.target.value })} />
                    <p className="col-span-2 text-xs text-gray-500">Al abrir el caso se crea una pestaña para cargar varios documentos hasta cerrarlo.</p>
                    <button disabled={guardandoFicha === 'accidente'} className="col-span-2 bg-teal-600 text-white rounded px-3 py-2 disabled:opacity-60">
                      {guardandoFicha === 'accidente' ? 'Abriendo…' : 'Abrir accidente'}
                    </button>
                  </form>
                </Bloque>

                <Bloque titulo="Otros documentos">
                  {(docsDe('otro') || []).length === 0 && <p className="text-gray-500 mb-2">Aún no hay otros expedientes.</p>}
                  <ListaDocs empresaId={empresaId} items={docsDe('otro')} onEliminar={(id) => borrarExpediente('otro', id, true)} />
                  <form onSubmit={onFichaOtro} className="mt-3 grid grid-cols-2 gap-2">
                    <input className="border rounded px-2 py-1.5 col-span-2" placeholder="Título del documento" value={fichaOtro.titulo} onChange={(e) => setFichaOtro({ ...fichaOtro, titulo: e.target.value })} />
                    <CampoArchivo archivo={fichaOtro.archivo} onChange={(archivo) => setFichaOtro({ ...fichaOtro, archivo })} etiqueta="Escaneo (PDF, JPG o PNG)" />
                    <button disabled={guardandoFicha === 'otro'} className="col-span-2 bg-teal-600 text-white rounded px-3 py-2 disabled:opacity-60">
                      {guardandoFicha === 'otro' ? 'Subiendo…' : 'Subir documento'}
                    </button>
                  </form>
                </Bloque>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

function ResumenFicha({ ficha }) {
  const cursos = Number(ficha.capacitaciones || 0);
  const docs = Number(ficha.documentos || 0);
  const abiertos = Number(ficha.incidentes_abiertos || 0);
  const accidentes = Number(ficha.accidentes || 0);
  const entregas = Number(ficha.epp?.entregas || 0);

  const items = [
    {
      label: 'SCTR',
      valor: labelEstado(ficha.sctr?.estado),
      extra: ficha.sctr?.fecha_vencimiento ? `Vence ${fmtFecha(ficha.sctr.fecha_vencimiento)}` : 'Sin vigencia',
      estado: ficha.sctr?.estado || 'sin',
    },
    {
      label: 'EMO',
      valor: labelEstado(ficha.emo?.estado),
      extra: ficha.emo?.fecha_vencimiento ? `Vence ${fmtFecha(ficha.emo.fecha_vencimiento)}` : 'Sin examen',
      estado: ficha.emo?.estado || 'sin',
    },
    {
      label: 'Tallas',
      valor: ficha.tallas?.completo
        ? `${ficha.tallas.talla_pantalon || '—'} / ${ficha.tallas.talla_polo || '—'} / ${ficha.tallas.talla_zapatos || '—'}`
        : 'Pendiente',
      extra: ficha.tallas?.completo ? 'Pantalón / polo / zapatos' : 'Falta registrar',
      estado: ficha.tallas?.completo ? 'vigente' : 'sin',
    },
    {
      label: 'EPP',
      valor: entregas ? `${entregas} registro${entregas === 1 ? '' : 's'}` : 'Sin registros',
      extra: entregas ? labelEstado(ficha.epp?.estado) : 'Pendiente',
      estado: ficha.epp?.estado || 'sin',
    },
    {
      label: 'Cursos',
      valor: cursos ? String(cursos) : '0',
      extra: cursos ? 'registrados' : 'Sin cursos',
      estado: cursos ? 'vigente' : 'sin',
    },
    {
      label: 'Documentos',
      valor: String(docs),
      extra: docs ? 'en la ficha' : 'Sin archivos',
      estado: docs ? 'vigente' : 'sin',
    },
    {
      label: 'Accidentes',
      valor: accidentes ? String(accidentes) : '0',
      extra: accidentes
        ? (abiertos ? `${abiertos} abierto${abiertos === 1 ? '' : 's'}` : 'Todos cerrados')
        : 'Sin accidentes',
      estado: abiertos ? 'alerta' : (accidentes ? 'vigente' : 'sin'),
    },
  ];

  return (
    <div className="mt-4 grid grid-cols-2 gap-2">
      {items.map((item) => {
        const tono = tonoResumen(item.estado);
        return (
          <div key={item.label} className={`rounded-xl border px-3 py-2 ${tono.caja} ${item.ancho ? 'col-span-2' : ''}`}>
            <div className="flex items-center gap-1.5 mb-1">
              <span className={`h-1.5 w-1.5 rounded-full ${tono.marca}`} />
              <p className={`text-[10px] font-semibold uppercase tracking-wide ${tono.texto}`}>{item.label}</p>
            </div>
            <p className={`text-sm font-semibold leading-tight ${tono.valor}`}>{item.valor}</p>
            <p className={`text-[11px] ${tono.texto}`}>{item.extra}</p>
          </div>
        );
      })}
    </div>
  );
}

function AlertaMini({ titulo, valor, color }) {
  const map = {
    red: 'border-red-200 bg-red-50 text-red-900',
    amber: 'border-amber-200 bg-amber-50 text-amber-900',
    orange: 'border-orange-200 bg-orange-50 text-orange-900',
    rose: 'border-rose-200 bg-rose-50 text-rose-900',
  };
  return (
    <div className={`rounded-2xl border p-4 ${map[color]}`}>
      <p className="text-xs uppercase tracking-wide opacity-80">{titulo}</p>
      <p className="text-2xl font-bold">{valor ?? 0}</p>
    </div>
  );
}

function SeccionCard({ titulo, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{titulo}</h3>
      {children}
    </div>
  );
}

function ListaSimple({ items, vacio, render }) {
  if (!items?.length) return <p className="text-sm text-gray-500">{vacio}</p>;
  return (
    <div className="divide-y divide-gray-100">
      {items.map((item, idx) => (
        <div key={item.id || idx} className="py-2">{render(item)}</div>
      ))}
    </div>
  );
}

function Bloque({ titulo, children }) {
  return (
    <section className="border border-gray-100 rounded-xl p-4">
      <h4 className="font-semibold text-gray-800 mb-2">{titulo}</h4>
      {children}
    </section>
  );
}

function ListaDocs({ empresaId, items, onEliminar, vacio = null }) {
  if (!items?.length) return vacio;
  return (
    <div className="mt-2 space-y-1">
      {items.map((d) => (
        <div key={d.id} className="flex items-center gap-2">
          <button
            type="button"
            className="text-teal-700 text-xs underline text-left"
            onClick={() => ssomaService.descargarDocumento(empresaId, d.id, d.archivo_nombre)}
          >
            {d.titulo || d.archivo_nombre}
          </button>
          {onEliminar && (
            <button type="button" className="text-[11px] text-red-600" onClick={() => onEliminar(d.id)}>
              Quitar archivo
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function TarjetaExpediente({
  titulo,
  detalle,
  documentos,
  empresaId,
  extra,
  editando,
  onEditar,
  onBorrar,
  onBorrarArchivo,
}) {
  return (
    <div className={`rounded-lg border px-3 py-2 ${editando ? 'border-teal-300 bg-teal-50/50' : 'border-gray-100 bg-slate-50/70'}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-gray-900">{titulo}</p>
          {detalle && <p className="text-xs text-gray-500">{detalle}</p>}
        </div>
        <div className="flex shrink-0 gap-1">
          {onEditar && (
            <button type="button" onClick={onEditar} className="text-xs text-teal-800 border border-teal-200 bg-white rounded px-2 py-1 hover:bg-teal-50">
              Editar
            </button>
          )}
          {onBorrar && (
            <button type="button" onClick={onBorrar} className="text-xs text-red-700 border border-red-200 bg-white rounded px-2 py-1 hover:bg-red-50">
              Borrar
            </button>
          )}
        </div>
      </div>
      {extra}
      <ListaDocs
        empresaId={empresaId}
        items={documentos}
        onEliminar={onBorrarArchivo}
        vacio={<p className="text-[11px] text-gray-400 mt-1">Sin archivo en este expediente.</p>}
      />
    </div>
  );
}

function SelectorInvolucrados({ fichas, actualId, seleccionados, busqueda, onBusqueda, onToggle, onQuitar, ayuda }) {
  const texto = String(busqueda || '').toLowerCase();
  const candidatos = fichas.filter((f) => {
    if (String(f.id) === String(actualId)) return false;
    if (!texto) return true;
    return `${f.nombre_completo} ${f.numero_documento}`.toLowerCase().includes(texto);
  });
  const elegidos = fichas.filter((f) => seleccionados.includes(String(f.id)));

  return (
    <div className="col-span-2 space-y-2">
      <p className="text-xs text-gray-600">
        {ayuda || 'Este trabajador ya va incluido. Busca y marca a los demás.'}
      </p>
      {elegidos.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {elegidos.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => onQuitar(f.id)}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-teal-50 text-teal-800 text-xs border border-teal-200"
            >
              {f.nombre_completo}
              <span aria-hidden>×</span>
            </button>
          ))}
        </div>
      )}
      <input
        type="search"
        value={busqueda}
        onChange={(e) => onBusqueda(e.target.value)}
        placeholder="Buscar por nombre o DNI"
        className="w-full border rounded px-2 py-1.5 text-sm"
      />
      <div className="max-h-40 overflow-y-auto border rounded-lg divide-y divide-gray-100">
        {candidatos.slice(0, 20).map((f) => {
          const marcado = seleccionados.includes(String(f.id));
          return (
            <label key={f.id} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer">
              <input type="checkbox" checked={marcado} onChange={() => onToggle(f.id)} />
              <span className="truncate">{f.nombre_completo}</span>
              <span className="text-xs text-gray-400">{f.numero_documento}</span>
            </label>
          );
        })}
        {candidatos.length === 0 && (
          <p className="px-3 py-2 text-xs text-gray-500">No hay coincidencias.</p>
        )}
      </div>
    </div>
  );
}

function CampoArchivo({ archivo, onChange, etiqueta = 'Escaneo PDF, JPG o PNG' }) {
  return (
    <label className="col-span-2 block border border-dashed border-gray-300 rounded-lg px-3 py-2 bg-gray-50">
      <span className="block text-xs text-gray-600 mb-1">{etiqueta}</span>
      <input
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        className="text-xs w-full"
        onChange={(e) => onChange(e.target.files?.[0] || null)}
      />
      {archivo && <span className="block text-xs text-teal-700 mt-1">{archivo.name}</span>}
    </label>
  );
}

export default Ssoma;
